// MOL-125 — a receipt's text, as Tesseract read it, into lines with their figures. A port of the
// prototype of MOL-114 (`hybrid.mjs` of its bench): the same rules give the same lines on the
// bench's readings, which a test holds. Amounts are counted in whole hundredths — what a till
// prints, whatever the currency's own minor unit — and quantities in thousandths; a ratio is a
// float only where it ranks candidates, never where it is money.
//
// The figures are code's, not a model's: OCR's own slips in this font (5 read as 6, 1 as 4) are
// undone by the line's arithmetic — paid + discount = quantity × shelf price — by the receipt's
// one card discount rate, and last by the printed total.
//
// The words it reads by — the total, the payment, the tax, the end of the list, the units, the
// currency after a sum — and the digits OCR confuses are the country's card (MOL-247): every pattern
// with a word in it is built from the card, once a card. The tills' layouts stay the parse's own.

import type { ReceiptPaidBy } from '#model/entities/receipt'
import { PARSED_FRACTION_DIGITS } from '#model/entities/receipt-card'
import type { DateOrder, PhotoReading } from '#model/entities/receipt-card'

/** A line's four figures: thousandths of its unit, hundredths of money. */
export interface LineFigures {
  readonly quantityMilli: number | null
  readonly priceHundredths: number | null
  readonly discountHundredths: number | null
  readonly sumHundredths: number | null
}

/** One row of a reading: its text and where it stands on the photo of its part. */
export interface TextRow {
  readonly text: string
  readonly part: number
  readonly line: number
}

export interface ReceiptTextLine {
  /** The item's name as printed, after the row number. */
  readonly printed: string
  /**
   * The class code printed on the line, or null: a customs heading of four digits for goods, a class
   * of services «56.10» for food service (MOL-226).
   */
  readonly hs: string | null
  /** The till's own article after the heading: «0401/1163909». */
  readonly sku: string | null
  /**
   * Thousandths of a kilogram, a litre or a piece; null when nothing readable fits the unit. An
   * Armenian till is read in kilograms and pieces; a litre comes from a Serbian journal (MOL-232).
   */
  readonly quantityMilli: number | null
  readonly unit: 'kg' | 'l' | 'piece'
  readonly priceHundredths: number | null
  readonly sumHundredths: number | null
  readonly discountHundredths: number | null
  /** The line's own arithmetic holds for the figures given. */
  readonly settled: boolean
  /**
   * Settled with every figure as the reading printed it (MOL-255): no swap of OCR, no figure worked out
   * — a price from the sum, a weight from the money, a count the line does not print — and nothing the
   * total gave. `settled` alone is held by a reading bent by two swaps a field; what the review is shown
   * is decided from this.
   */
  readonly asPrinted: boolean
  /**
   * Put right by the digits the till's font confuses, on a model's reading proven whole by it (MOL-290):
   * the one reading of the line that holds its arithmetic and the card's rate, every line then meeting a
   * total two places vouch for. Shown with «проверьте», never as printed.
   */
  readonly repaired?: boolean
  /**
   * The line's figures as the reader gave them, before any swap and with nothing worked out (MOL-291): what
   * the shop's memory sets its shelf price against. None on a line no article keys — a table's, a journal's.
   */
  readonly read?: LineFigures
  /**
   * What the shop's memory said of the line (MOL-291): its price `confirmed` a line as printed, `repaired`
   * one that did not hold, or `doubted` one as printed one confused digit off it — emptied by the check.
   */
  readonly remembered?: 'confirmed' | 'repaired' | 'doubted'
  /**
   * The rows the line may be cut out from for the reader's training (MOL-169): its figures, and its
   * name only when the item's number was read on it. A name row is the first row above the figures,
   * and above the first item that is the head — the VAT, a buyer's name — when OCR lost the name: the
   * number is what says it is an item. A table gives its heading row only: its last row runs on into
   * the total when OCR misreads the total's word.
   */
  readonly rows: readonly TextRow[]
}

/**
 * How the lines were found: «Ереван Сити»'s card, Dog City's table, the class code (MOL-226) — or a
 * till's section with no items printed at all, the sole trader's receipt (MOL-227).
 */
export const RECEIPT_LAYOUTS = ['card', 'table', 'class', 'department'] as const
export type ReceiptLayout = (typeof RECEIPT_LAYOUTS)[number]

export interface ReceiptText {
  readonly layout: ReceiptLayout
  /** The seller's tax number (ՀՎՀՀ), eight digits. */
  readonly tin: string | null
  /** The day and time printed, as written on the receipt: `YYYY-MM-DD`, `HH:MM`. */
  readonly date: string | null
  readonly time: string | null
  readonly receiptNo: string | null
  /**
   * The total two sources vouch for — the lines that met it, or two places printed (MOL-244) — else null:
   * what the review shows and the trip's money may take.
   */
  readonly totalHundredths: number | null
  /**
   * The total as the reading found it in one place, vouched for or not: only what «Прочитали не всё»
   * measures the lines against (MOL-244), never a total shown or recorded.
   */
  readonly readTotalHundredths: number | null
  /** The lines add up to the printed total. */
  readonly balanced: boolean
  /**
   * The card's discount of the transaction, sent to the card as a bonus rather than taken off at the till
   * («Զեղչ գործարքից», «Ереван Сити», MOL-289): the lines make the total less it, and the total is what
   * left the card. Only a model's reading names it.
   */
  readonly cashbackHundredths?: number | null
  /**
   * The bonus this receipt is partly paid with («Կանխավճարի մարում», MOL-289): the trip's money is the
   * total less it — what the card's bonus covered was recorded when it was sent to the card.
   */
  readonly redeemedHundredths?: number | null
  readonly lines: readonly ReceiptTextLine[]
}

// «1 260,5» — what OCR printed — as a number; NaN for what is not one, as the prototype had it.
const number = (text: string): number => Number(text.replace(/\s/g, '').replace(',', '.'))
const hundredthsOf = (text: string): number => Math.round(number(text) * 100)
const milliOf = (text: string): number => Math.round(number(text) * 1000)
const finite = (value: number | null | undefined): number | null =>
  value === null || value === undefined || !Number.isFinite(value) ? null : value

/** The patterns of the parse with a country's words in them, built from its card (MOL-247). */
interface Lexicon {
  /** Digits OCR confuses in the tills' font; a figure is tried with each of them swapped in. */
  readonly swaps: Readonly<Record<string, string>>
  /** Letters Tesseract puts where the font has digits, before a digit or a point. */
  readonly digitLike: Readonly<Record<string, string>>
  readonly digitLikeAt: RegExp
  readonly cardTotal: RegExp
  readonly cardTin: RegExp
  readonly cardNo: RegExp
  readonly tableEnd: RegExp
  readonly tableTotal: RegExp
  readonly tableWeighed: RegExp
  readonly tableTin: RegExp
  readonly tableNo: RegExp
  readonly tableReceiptNo: RegExp
  readonly classTail: string
  readonly classMark: RegExp
  readonly classEnd: RegExp
  readonly classTotal: RegExp
  readonly classUnit: RegExp
  readonly classTin: RegExp
  readonly classNo: RegExp
  readonly classHead: RegExp
  readonly kg: RegExp
  readonly kgWhole: RegExp
  readonly terminalSum: RegExp
  readonly terminalUnit: RegExp
  readonly tillWords: RegExp
  readonly oneLetterUnit: RegExp
  readonly letter: RegExp
  readonly capitals: RegExp
  readonly small: RegExp
  readonly startsSmall: RegExp
  readonly itemMark: RegExp
  readonly tinWord: RegExp
  readonly fiscal: RegExp
  readonly fiscalNo: RegExp
  readonly sectionTax: RegExp
  readonly totalKept: RegExp
  readonly discountKept: RegExp
  readonly sinceStart: RegExp
  readonly paid: RegExp
  readonly cash: RegExp
  readonly cashless: RegExp
  readonly redeemed: RegExp
}

const lexicons = new WeakMap<PhotoReading, Lexicon>()

/** The parse's patterns for a card, built once: a reading costs no compiling. */
function lexiconOf(reading: PhotoReading): Lexicon {
  const known = lexicons.get(reading)
  if (known !== undefined) return known
  // a card names only formats of numbers the parse reads: amounts are hundredths throughout, and three
  // decimals read as two would be every sum wrong (MOL-248 adds the third with the reading of it)
  const unread = reading.numbers.find((f) => !PARSED_FRACTION_DIGITS.includes(f.fractionDigits))
  if (reading.numbers.length === 0 || unread !== undefined) {
    throw new RangeError(
      `a card's formats of numbers are not the parse's: ${JSON.stringify(reading.numbers)}`,
    )
  }
  const w = reading.words
  const u = w.units
  // a word that captures moves the figures the parse reads after it by their groups: the total read as the
  // word, NaN (adversarial А1) — refused, as a format it cannot read is
  const fragments = [
    ...[w.total, w.totalKept, w.discountKept, w.sinceStart, w.cashback, w.taxed, w.redeemed],
    ...[w.control, w.fiscalSign, w.tin],
    ...[w.fiscal, w.fiscalNumber, w.receiptNumber, w.sumSign, ...w.paid, ...w.sectionTax],
    ...[...w.cash, ...w.cashless],
    ...w.tinMisread,
    ...[...w.head, ...w.currency, ...Object.values(u)],
  ]
  const captures = fragments.find(
    (fragment) => (new RegExp(`(?:${fragment})|`, 'u').exec('')?.length ?? 1) > 1,
  )
  if (captures !== undefined) throw new RangeError(`a card's word captures: ${captures}`)
  // A word is a fragment as OCR reads it, so it goes in a group of its own (adversarial А1): a form after
  // «|» would otherwise take the rest of the pattern for its own and find the word with no sum after it. And
  // every pattern a word goes into is read with `u`, as the word is checked: without it «\p{L}» is the
  // letters «p{L}», and a word the card's test admits was lost by half the layouts (round 2, Б1).
  const word = (fragment: string): string => `(?:${fragment})`
  const any = (forms: readonly string[]): string => word(forms.join('|'))
  // characters set into a class: the four a class reads as its own syntax are escaped (adversarial А4)
  const inClass = (chars: string): string => chars.replace(/[\\\]^-]/g, '\\$&')
  // What OCR keeps of «Դաս» — «Դաս.», «աս,», «հաս ‘» — and the class after it: «56.10», «56 10», «5610».
  // «աս» is the word «Դաս»: «Դ», «դ» or «հ» before it, or no letter — never inside a name, «Կվաս». And the
  // class is no figure that goes on (reviews 1, 2): not an amount after it — «1 հաս 1300 1300», «Բաքեթ
  // 5610 5610», «5610 00х10», «5610.00x1.0» — nor a unit of volume or weight, glued or apart, «1000 մլ».
  // Letters right after it are the article's, «56.10Ն/Կ 745030»: OCR eats the comma and the space.
  const classTail = [
    '(?!\\d',
    // a price's decimals, «5610.00x» — an article glued with its comma, «56.10,745030», is no price (review 3)
    '|[.,]\\d{1,2}(?!\\d)',
    '|\\s*[xх×*]',
    // the same figure again — a price and its sum, «1 հաս 1300 1300», «Բաքեթ 5610 5610» — or a figure
    // before «x»: the «class» was the line's price. Anything else after it is the code's own junk, «19»,
    // «20-», or a count before its unit, «56.10 1 հատ» (reviews 3, 4)
    `|\\s+\\1\\s?[.,]?\\s?\\2(?:[.,]\\d{1,2})?(?![\\d.,])(?!\\s*${any([u.piece, u.kg])})`,
    '|\\s+\\d{1,4}(?:[.,]\\d+)?\\s*[xх×*]',
    `|\\s*${any([u.ml, u.litre, u.gram, u.kg])}(?!\\p{L}))`,
  ].join('')
  // «Դաս», «հաս», «աս», and «Դ» misread as its look-alikes «Գ», «Ղ» (review 4) — a letter glued before
  // the word stays, «րԴաս» (review 3); «Կվաս» is a name
  const classMark = new RegExp(
    `(?:(?<=[ԴդհԳգՂղ])|(?<!\\p{L}))(?<!\\d\\s?հ)աս[^\\p{L}\\d]{0,4}(\\d{2})\\s?[.,]?\\s?(\\d{2})${classTail}`,
    'u',
  )
  const lexicon: Lexicon = {
    swaps: reading.swaps,
    digitLike: reading.digitLike,
    digitLikeAt: new RegExp(
      `[${inClass(Object.keys(reading.digitLike).join(''))}](?=[\\d,.])`,
      'gu',
    ),
    cardTotal: new RegExp(`${word(w.total)}\\s+(\\d[\\d ]*\\.\\d{1,2})\\b`, 'u'),
    cardTin: new RegExp(`${word(w.tin)}.{0,4}:?\\s*\\S+|:\\s*0\\d{7}`, 'u'),
    cardNo: new RegExp(`${word(w.fiscal)}\\s+\\S+\\s+(\\d{6,})`, 'u'),
    tableEnd: new RegExp(any([w.total, w.control, w.fiscalSign]), 'u'),
    tableTotal: new RegExp(`${word(w.total)}:?\\s+(\\d[\\d ]*[.,]\\d{2})`, 'u'),
    // the table prints its unit in brackets, in either case: «20կգ(Կգ)», «կԳ(ԿԳ)»
    tableWeighed: new RegExp(`${word(u.kg)}\\)`, 'iu'),
    tableTin: new RegExp(`${any([w.tin, ...w.tinMisread])}\\S{0,2}\\s*(\\d{8})`, 'u'),
    tableNo: new RegExp(`${word(w.fiscal)}\\s*\\S*\\s+(\\d{6,})`, 'u'),
    tableReceiptNo: new RegExp(`${word(w.receiptNumber)}:?\\s*(\\d[\\d ]{2,})`, 'u'),
    classTail,
    classMark,
    classEnd: new RegExp(any([w.control, w.total]), 'u'),
    classTotal: new RegExp(`${word(w.total)}:?\\s+(\\d[\\d ]*(?:[.,]\\d{1,2})?)(?![\\d.,])`, 'u'),
    // a count before its unit: «1հատ», «4 հատ», «0.742 կգ»
    classUnit: new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*(${word(u.piece)}|${word(u.kg)})`, 'u'),
    classTin: new RegExp(`${word(w.tin)}\\S{0,2}\\s*(\\d{8})(?!\\d)`, 'u'),
    classNo: new RegExp(
      `${word(w.fiscal)}\\S*(?:\\s+\\S*${word(w.fiscalNumber)}\\S*)?\\s+(\\d{6,})`,
      'u',
    ),
    // words of a fiscal head that are no dish: the fiscal mark, the tax number, the cashier, the section,
    // the address, the till
    classHead: new RegExp(any([w.fiscal, w.tin, ...w.head]), 'u'),
    // a kilogram, read as the card's fragment wherever a line is weighed (adversarial А2)
    kg: new RegExp(word(u.kg), 'u'),
    kgWhole: new RegExp(`^${word(u.kg)}$`, 'u'),
    // the terminal's figures: «price x qty unit = sum դրամ»; OCR reads «=» as «-»
    terminalSum: new RegExp(`[-=—–]\\s*(\\d+(?:[.,]\\d+| \\d{2})?)\\s*${word(w.sumSign)}`, 'u'),
    terminalUnit: new RegExp(`\\s*${any([u.pieceCut, u.kg])}\\s*$`, 'u'),
    tillWords: new RegExp(`${any([u.piece, u.kg, ...w.currency])}(?=\\s|$)`, 'gu'),
    oneLetterUnit: new RegExp(`^${any([u.litre, u.gram])}$`, 'u'),
    // the letters are ranges of a class, as the card names them
    letter: new RegExp(`[${w.letters.any}]`, 'u'),
    capitals: new RegExp(`[${w.letters.capital}]{3}`, 'u'),
    small: new RegExp(`[${w.letters.small}]`, 'u'),
    // a row whose first letter is a small one: a name run on from the row above, «նալ», «դե լյուքս»
    startsSmall: new RegExp(`^[^\\p{L}]*[${w.letters.small}]`, 'u'),
    // What only an item prints: the class code, a customs heading with its item's name «(3824) ՏՈՖՈՒ», a
    // till's article «0401/1163909» — six digits and more, the bench's 386 of 394 — a table's heading «Անուն
    // Քան Գին Գումար». Dog City and KFC print «Բաժին» too, over their items. A phone in the trader's head is
    // none of them, «(0312) 5-12-34», «0312/51234», wherever it stands and whichever reading lost the section
    // (adversarial А2, round 2 Б2, review 2 № 10).
    itemMark: new RegExp(
      `${classMark.source}|\\(\\d{4}\\)\\s*\\p{L}|(?<!\\d)\\d{4}\\s*/\\s*\\d{6,}|Անուն.{0,20}Քան`,
      'u',
    ),
    // the tax number after its word, anywhere — never the receipt's own number «ԿՀ: 00000049»
    tinWord: new RegExp(`${word(w.tin)}\\S{0,2}\\s*(\\d{8})(?!\\d)`, 'u'),
    fiscal: new RegExp(word(w.fiscal), 'iu'),
    // «ՖԻՍԿԱԼ ՀԱՄԱՐ 04143299», in capitals as often as not
    fiscalNo: new RegExp(`${word(w.fiscal)}\\S*\\s+\\S*\\s*(\\d{8})(?!\\d)`, 'iu'),
    sectionTax: new RegExp(any(w.sectionTax), 'u'),
    totalKept: new RegExp(word(w.totalKept), 'u'),
    discountKept: new RegExp(word(w.discountKept), 'u'),
    sinceStart: new RegExp(word(w.sinceStart), 'u'),
    paid: new RegExp(any(w.paid), 'u'),
    cash: new RegExp(any(w.cash), 'u'),
    cashless: new RegExp(any(w.cashless), 'u'),
    redeemed: new RegExp(word(w.redeemed), 'u'),
  }
  lexicons.set(reading, lexicon)
  return lexicon
}

function variants(swaps: Readonly<Record<string, string>>, text: string, depth = 2): string[] {
  const out = new Set([text])
  let edge = [text]
  for (let k = 0; k < depth; k++) {
    const next: string[] = []
    for (const value of edge) {
      for (let i = 0; i < value.length; i++) {
        const swapped = swaps[value.charAt(i)]
        if (swapped === undefined) continue
        const variant = value.slice(0, i) + swapped + value.slice(i + 1)
        if (!out.has(variant)) {
          out.add(variant)
          next.push(variant)
        }
      }
    }
    edge = next
  }
  // «1,3124q» is 1,312 kg with «կգ» read as «4q»: a weight has three decimals at most
  const weight = /^(\d+[.,]\d{3})\d$/.exec(text)
  if (weight?.[1] !== undefined) out.add(weight[1])
  return [...out]
}

// A till prints «=» before an amount, and OCR reads the sign as a digit: «=119.00» as «2119.00»
// (MOL-228, H9). The amount without it, if it is one: never nothing, a line's sum is never zero.
function signless(text: string): string | null {
  const rest = /^\d(\d+[.,]\d{2})$/.exec(text)?.[1]
  return rest === undefined || hundredthsOf(rest) === 0 ? null : rest
}

const diff = (a: string, b: string): number =>
  a.length !== b.length ? 1 : Array.from(a).filter((c, i) => c !== b.charAt(i)).length

// «130 603,87» is «1Հտ» glued to 603,87 as often as it is a thousand-group: try without it too.
function dropGroup(text: string): string[] {
  const glued = /^(\d{1,3}) (\d{3}[.,].*)$/.exec(text)
  return glued?.[2] !== undefined ? [glued[2]] : []
}

interface Candidate {
  readonly qty: number
  readonly paid: number
  readonly disc: number
  readonly price: number
  readonly swaps: number
  readonly rate: number
  /** A sign read as a digit taken off (MOL-246): the total's to take, never a line's own reading. */
  readonly sliced?: true
  /** A count no figure of the line printed, one of the twenty tried: worked out, never as printed (MOL-255). */
  readonly counted?: true
}

// The till rounds to a few luma: 609,97 paid for 610.
export const TOLERANCE_HUNDREDTHS = 3

/**
 * Past this many combinations a line's figures are taken as read, with no reading tried: the
 * search grows as a power of the digits OCR can confuse in every field, and it runs in the API's
 * process — twelve glued twelve-digit figures held it for half a minute (review, MOL-125). The
 * bench's worst line has 55 176 (am-03), a quarter of it.
 */
export const LINE_COMBINATIONS_MAX = 200_000

/**
 * And past this many over one reading the lines left are taken as read: a line under the ceiling
 * still costs a tenth of a second, and twelve of them stalled every request of the API for one. The
 * bench's busiest reading tries 122 161 (am-03).
 */
export const READING_COMBINATIONS_MAX = 300_000

/**
 * What every line may try whatever the reading has spent (review Р15): rows of an item's shape above
 * the list — a stamp, a code, a smudge — would otherwise drain the budget before the first item, and
 * every real line would keep its confusions. A real line tries a few thousand (am-05: 1 694 at most).
 * The floors draw on a budget of their own (review Р18), or four hundred rows of junk under it would
 * buy seconds again.
 */
export const LINE_COMBINATIONS_FLOOR = 10_000
export const FLOOR_COMBINATIONS_MAX = 200_000

/** What a reading may still try: its own budget, and the floors' beside it — and the swaps it tries. */
interface Budget {
  left: number
  floors: number
  readonly swaps: Readonly<Record<string, string>>
}

// Takes `combinations` from a budget, or says the line is to be taken as read.
function afford(budget: Budget, combinations: number): boolean {
  if (combinations > LINE_COMBINATIONS_MAX) return false
  if (combinations <= budget.left) {
    budget.left -= combinations
    return true
  }
  if (combinations <= LINE_COMBINATIONS_FLOOR && combinations <= budget.floors) {
    budget.floors -= combinations
    return true
  }
  return false
}

const holds = (qty: number, paid: number, disc: number, price: number): boolean =>
  Math.abs(paid + disc - Math.round((qty * price) / 1000)) <= TOLERANCE_HUNDREDTHS

/**
 * The searches of a line's candidates made in this process — read by tests alone (MOL-265). «A line's
 * candidates are found once» (`Laid`, MOL-244) is held by this count: a second search is twice the work,
 * and no bound on a clock tells twice from once on CI.
 */
export const candidateSearches = { count: 0 }

// One pass over the figures of a line: every combination that satisfies paid + discount =
// quantity × price is a candidate; the receipt's own discount rate then picks among them (a card
// discount is one percentage), and after it the fewest swaps.
function candidates(
  budget: Budget,
  qtyS: string,
  paidS: string,
  discS: string,
  priceS: string | null,
): Candidate[] {
  candidateSearches.count += 1
  const out: Candidate[] = []
  const qtys = variants(budget.swaps, qtyS)
  const paids = [paidS, ...dropGroup(paidS)].map((p0) => ({ p0, all: variants(budget.swaps, p0) }))
  const discs = variants(budget.swaps, discS)
  const prices = priceS === null ? [null] : variants(budget.swaps, priceS)
  const combinations =
    qtys.length * paids.reduce((n, p) => n + p.all.length, 0) * discs.length * prices.length
  if (!afford(budget, combinations)) return out
  for (const q of qtys) {
    for (const { p0, all } of paids) {
      for (const p of all) {
        for (const d of discs) {
          for (const pr of prices) {
            const qty = milliOf(q)
            const paid = hundredthsOf(p)
            const disc = hundredthsOf(d)
            const price = pr === null ? Math.round(((paid + disc) * 1000) / qty) : hundredthsOf(pr)
            const swaps =
              diff(q, qtyS) +
              diff(p, p0) +
              diff(d, discS) +
              diff(pr ?? '', priceS ?? '') +
              (p0 !== paidS ? 1 : 0) +
              (pr === null ? 1 : 0)
            if (holds(qty, paid, disc, price)) {
              out.push({ qty, paid, disc, price, swaps, rate: disc / (paid + disc) })
            }
          }
        }
      }
    }
  }
  return out
}

/**
 * The line's sum or its price read without the sign before it (MOL-246), every other figure as read:
 * tried only where no reading of the line holds, and only the printed total takes it (review, MOL-246).
 * Beside the swaps, a price that lost a digit beat the sum's own swap at the same cost — 1500 × 3 read
 * «=1500» became 500 × 3; with swaps of the other figures it «fixed» all three of one line; and taken
 * as the line's own it read «2119,00» as 119,00 where the total said 2 119,00, and tied a reading that
 * misread the line with one that read it right. Without a total that meets it the line stays as read.
 */
function sliced(
  budget: Budget,
  qtyS: string,
  paidS: string,
  discS: string,
  priceS: string | null,
): Candidate[] {
  if (priceS === null) return []
  const tries = [
    [signless(paidS), priceS],
    [paidS, signless(priceS)],
  ].filter((t): t is [string, string] => t[0] !== null && t[1] !== null)
  if (!afford(budget, tries.length)) return []
  const [qty, disc] = [milliOf(qtyS), hundredthsOf(discS)]
  return tries
    .map(([p, pr]) => ({ paid: hundredthsOf(p), price: hundredthsOf(pr) }))
    .filter(({ paid, price }) => holds(qty, paid, disc, price))
    .map(({ paid, price }) => ({
      qty,
      paid,
      disc,
      price,
      swaps: 1,
      rate: disc / (paid + disc),
      sliced: true as const,
    }))
}

// What a reading may still spend: a search it paid for spent some, one past its ceiling none.
const funds = (budget: Budget): number => budget.left + budget.floors

function mode(values: readonly number[]): number | null {
  const counts = new Map<number, number>()
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1)
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
}

const off = (a: number, rate: number): number => Math.round(Math.abs(a - rate) * 1000)

/**
 * The rate the lines as read share, for the total to choose by (review Р20): a till that prints no
 * shelf price has no line read with no swap — its price is worked out — so `settle` has no rate to
 * order by, and two fixes of one digit cost the total the same. The lines with their fewest swaps
 * still say the rate, and a fix that leaves a line off it costs a little more there. Only there: as
 * an order of a line's readings it «fixed» am-01's right sum to fit (65 of 162 on the bench).
 */
function asReadRate(all: readonly Candidate[][]): number | null {
  return mode(
    all
      .flatMap((list) => {
        // a loop, not `Math.min(...)`: a list of ninety thousand readings is past the stack (review 10)
        const fewest = list.reduce((least, x) => Math.min(least, x.swaps), Infinity)
        return list.filter((x) => x.swaps === fewest).slice(0, 1)
      })
      .map((x) => Math.round(x.rate * 1000) / 1000),
  )
}

// What a reading off the rate the lines share costs the total beyond its swaps.
const OFF_RATE_COST = 0.25

function settle(all: Candidate[][]): Candidate[][] {
  const clean = all
    .flatMap((list) => list.filter((x) => x.swaps === 0).slice(0, 1))
    .map((x) => Math.round(x.rate * 1000) / 1000)
  const rate = mode(clean)
  return all.map((list) =>
    list.sort(
      (a, b) =>
        Number(a.swaps > 0) - Number(b.swaps > 0) ||
        (rate === null ? 0 : off(a.rate, rate) - off(b.rate, rate)) ||
        a.swaps - b.swaps,
    ),
  )
}

// The digits of an amount as a till prints them: 128991 for 1 289,91.
const printedDigits = (hundredths: number): string =>
  String(Math.floor(hundredths / 100)) + String(hundredths % 100).padStart(2, '0')

// The blank must still look like what is printed there: the rest differs from the printed figure
// by one digit at most (1 289,91 read as «1 209,91»), or anything could be poured into it.
function looksLike(rest: number, printed: string): boolean {
  const digits = printedDigits(rest)
  return [digits, digits.endsWith('00') ? digits.slice(0, -2) : null].some((d) => {
    if (d === null) return false
    const head = printed.slice(0, d.length)
    return (
      head.length === d.length && Array.from(d).filter((c, i) => c !== head.charAt(i)).length <= 1
    )
  })
}

interface State {
  readonly sum: number
  readonly cost: number
  // the line's pick and the state it was made from: a chain, not a copied list — the copy made the
  // search quadratic in the lines and a receipt of fifteen of them held the API for a minute
  readonly pick: Candidate | null
  readonly prev: State | null
  readonly blank: number
  // the total's one answer is given: the blank line's rest, or a slice (MOL-246, review В1)
  readonly answered: boolean
  // another reading of the same cost reached the same sum: the total cannot tell which (review Р20)
  readonly tied: boolean
}

/**
 * Past this many steps the total no longer chooses among the readings (review Р14, 8): the lines keep
 * their first reading, as when no total was read. The search runs in the API's process; the bench's
 * longest takes 94 445 (am-03), ninety ordinary lines of a till with no shelf price fit, and the worst
 * shape the review found — four hundred rows of junk and a total — costs half a second a reading.
 */
export const RECONCILE_STEPS_MAX = 500_000

/**
 * The readings the search carries from one line to the next: the cheapest — fewest swaps — when there
 * are more (review Р14, 8). A till that prints no shelf price lets every swap of a line «fit» the line,
 * and the readings multiply by dozens a line; the cheapest are the ones the total would choose among.
 * The bench carries up to 9 732 (am-03) and reads the same with three hundred; thirty such lines are
 * settled against their total in a tenth of a second, ninety in under a third.
 */
export const RECONCILE_STATES_MAX = 300

// What the total's one answer costs — the blank line's rest or a slice: dearer than any line's own reading.
const ANSWER_COST = 4

function picksOf(state: State): (Candidate | null)[] {
  const picks: (Candidate | null)[] = []
  // every state but the start made a pick: the start is the one with nothing before it
  for (let at = state; at.prev !== null; at = at.prev) picks.push(at.pick)
  return picks.reverse()
}

// The printed total is the last judge. OCR can misread both columns alike — «60 60» for «50 50» —
// and the line's arithmetic holds either way; the reading whose lines add up to the total wins,
// changing as few lines as possible. One line with no reading at all (a discount the till did not
// print, a figure OCR lost) takes what the total leaves.
function reconcile(
  lists: readonly Candidate[][],
  total: number | null,
  printed: readonly string[],
  rate: number | null,
): { picks: (Candidate | null)[]; balanced: boolean; tied: boolean } {
  // a line's first reading of its own: a slice is never one (MOL-246)
  const first = lists.map((list) => list.find((x) => x.sliced !== true) ?? null)
  if (total === null || !Number.isFinite(total))
    return { picks: first, balanced: false, tied: false }

  // what the lines after each one come to as first read: a reading whose sum with them lands nearer
  // the total is kept before another of the same cost when the beam is full (review П11)
  const ahead = lists.map((_, i) => first.slice(i + 1).reduce((sum, x) => sum + (x?.paid ?? 0), 0))
  const start: State = {
    sum: 0,
    cost: 0,
    pick: null,
    prev: null,
    blank: -1,
    answered: false,
    tied: false,
  }
  let states = new Map<string, State>([['0|false|false', start]])
  const owned = lists.map(
    (list) => new Set(list.filter((x) => x.sliced !== true).map((x) => x.paid)),
  )
  let steps = 0
  for (const [i, list] of lists.entries()) {
    const next = new Map<string, State>()
    const put = (state: State): void => {
      steps += 1
      const key = `${String(state.sum)}|${String(state.blank >= 0)}|${String(state.answered)}`
      const was = next.get(key)
      if (was === undefined || state.cost < was.cost) next.set(key, state)
      else if (state.cost === was.cost && !was.tied) next.set(key, { ...was, tied: true })
    }
    for (const state of states.values()) {
      if (steps > RECONCILE_STEPS_MAX) break
      // a line with no reading of its own takes the rest of the total, or a slice (MOL-246); at one
      // cost and one sum the rest, put first, wins: the sum is the same, and the person looks either way
      // (review 13)
      if (list.every((x) => x.sliced === true)) {
        if (!state.answered) {
          put({
            sum: state.sum,
            cost: state.cost + ANSWER_COST,
            pick: null,
            prev: state,
            blank: i,
            answered: true,
            tied: state.tied,
          })
        }
        if (list.length === 0) continue
      }
      const seen = new Set<number>()
      for (const [k, x] of list.entries()) {
        // a slice whose sum the line reads of its own changes no sum: the line's own reading stands,
        // and the total's one answer is left to a line that has none (review 12)
        if (x.sliced === true && owned[i]?.has(x.paid) === true) continue
        if (seen.has(x.paid)) continue
        seen.add(x.paid)
        const sum = state.sum + x.paid
        if (sum > total) continue
        // a slice is the total's answer as the blank's rest is, at its price, and the receipt has one
        // (review В1, В2): two lines with none of their own could share the total between a slice and
        // a rest, and a slice at the price of a swap beat a right rest with two swaps elsewhere
        if (x.sliced === true) {
          if (!state.answered) {
            put({
              sum,
              cost: state.cost + ANSWER_COST,
              pick: x,
              prev: state,
              blank: state.blank,
              answered: true,
              tied: state.tied,
            })
          }
          continue
        }
        put({
          sum,
          cost:
            state.cost +
            x.swaps +
            (k > 0 ? 0.5 : 0) +
            (rate !== null && off(x.rate, rate) > 5 ? OFF_RATE_COST : 0),
          pick: x,
          prev: state,
          blank: state.blank,
          answered: state.answered,
          tied: state.tied,
        })
      }
    }
    if (steps > RECONCILE_STEPS_MAX) return { picks: first, balanced: false, tied: false }
    states =
      next.size > RECONCILE_STATES_MAX
        ? new Map(
            [...next]
              .sort(
                (a, b) =>
                  a[1].cost - b[1].cost ||
                  Math.abs(total - a[1].sum - (ahead[i] ?? 0)) -
                    Math.abs(total - b[1].sum - (ahead[i] ?? 0)),
              )
              .slice(0, RECONCILE_STATES_MAX),
          )
        : next
  }

  let best: State | null = null
  for (const state of states.values()) {
    const rest = total - state.sum
    const ok =
      state.blank < 0
        ? rest === 0 || Math.abs(rest) <= 1
        : rest > 0 && looksLike(rest, printed[state.blank] ?? '')
    if (ok && (best === null || state.cost < best.cost)) best = state
  }
  return best === null
    ? { picks: first, balanced: false, tied: false }
    : { picks: picksOf(best), balanced: true, tied: best.tied }
}

/**
 * The lines' readings judged by the printed total: the pick of each, whether they met it, what the
 * one line with no reading takes, and which lines the total changed or could not tell apart.
 */
function judged(
  found: Candidate[][],
  total: number | null,
  printed: readonly string[],
): {
  picks: (Candidate | null)[]
  balanced: boolean
  blankSum: number | null
  doubt: ReadonlySet<number>
  sliced: boolean
} {
  const lists = settle(found)
  // the shared rate counts in the total's choice only where `settle` had none to order by
  const own = lists.map((list) => list.filter((x) => x.sliced !== true))
  const shared = own.some((list) => list.some((x) => x.swaps === 0)) ? null : asReadRate(own)
  // slices only where no way without them meets the total (review Д1): beside a blank and another line's
  // swaps, a wrong slice met it cheaper by leaving that line's misread as read
  const plain = reconcile(own, total, printed, shared)
  const { picks, balanced, tied } =
    plain.balanced || own.every((list, i) => list.length === lists[i]?.length)
      ? plain
      : reconcile(lists, total, printed, shared)
  // what the total leaves goes to the one line with no reading only when the lines met the total —
  // otherwise «the rest» is a guess, and a line that took it would cover a receipt barely read
  // (review Р6)
  const blankSum =
    balanced && total !== null && picks.filter((x) => x === null).length === 1
      ? total - picks.reduce((a, x) => a + (x?.paid ?? 0), 0)
      : null
  // A line the total changed from its reading is not vouched for (review Р27): the total sets any
  // difference right by one swap in some line whose own arithmetic holds — a line read twice or lost
  // at a seam included — so «balanced» is «balanced as read», and a change by the total is one look of
  // the person's. In a tie the total could have made its change in another line just as well: every
  // line with a reading of its own that differs by the same amount is in doubt too (review Р20, Р24).
  const moves = picks.flatMap((pick, i) => {
    const first = own[i]?.[0]
    return pick !== null && pick.sliced !== true && first !== undefined && pick !== first
      ? [[i, pick.paid - first.paid]]
      : []
  })
  const doubt = new Set<number>(moves.map(([i]) => i ?? -1))
  // a slice the total took is the total's answer, as the blank's rest is: one look of the person's, never
  // a line «as read» — or a total read wrong in one place would vouch for itself through the slice it chose
  // (review Г1; MOL-244, В-1 «а»)
  picks.forEach((pick, i) => {
    if (pick?.sliced === true) doubt.add(i)
  })
  if (tied) {
    own.forEach((list, j) => {
      const first = list[0]
      if (first === undefined || doubt.has(j)) return
      if (moves.some(([, move]) => list.some((x) => x !== first && x.paid - first.paid === move))) {
        doubt.add(j)
      }
    })
  }
  return { picks, balanced, blankSum, doubt, sliced: picks.some((pick) => pick?.sliced === true) }
}

const ITEM = /^\s*(\d{1,2})\s*[.,]?\s*(\S.*)$/
// An item's number for cutting a name row out (review Р16): one or two digits and then a letter — a
// date «01.10.2026 …» or a phone «091 123456 …» above the list is not an item, and its row is the head.
const NUMBERED = /^\s*\d{1,2}(?!\d)\s*[.,]?\s*\p{L}/u
// «8506/119112 1Հտ 760,75/89,25 850» — code, quantity, paid/discount, shelf price (old/new).
const FIGURES =
  /^\s*(\d{1,4})\s*\/\s*([\d.]{5,})\s+(.*?)\s*(\d[\d ]*[.,]\d{1,2})\s*\/\s*(\d[\d ]*(?:[.,]\d{1,2})?)(?:\s+(?:\d[\d ]*\/\s*)?(\d[\d ]*\d|\d))?\s*$/
// A shadow or the paper's edge puts junk at both ends of a row: «=, 0401/1183903 2 732,17,4 370 М».
// A row that has a code «dddd/ddddd» keeps what lies from the code to its last digit.
function margins(row: string): string {
  const code = /\d{4}\s*\/\s*\d{5}/.exec(row)
  if (code === null) return row
  const end = row.search(/\d[^\d]*$/)
  return row.slice(code.index, end + 1)
}

// Letters Tesseract puts where this font has digits, put back.
const digits = (row: string, lx: Lexicon): string =>
  margins(row).replace(lx.digitLikeAt, (c) => lx.digitLike[c] ?? c)

const WEIGHT = /(\d+[.,]\d+)\s*\S*$/

// A receipt with no card discount (a Yandex Eats order) prints «0401/1163909 2Հտ 740 370»: code,
// quantity, paid, shelf price — no «/discount».
const PLAIN = /^\s*(\d{1,4})\s*\/\s*([\d.]{5,})\s+(\S+)\s+(\d[\d ,.]*\d)\s*$/

interface Found {
  readonly head: string
  readonly hs: string
  readonly sku: string
  readonly qtyS: string
  readonly weighed: boolean
  /** The count of pieces a plain row prints before its unit, «2Հտ»; null where OCR lost it. */
  readonly count: number | null
  readonly plain: readonly string[] | null
  readonly paidS: string
  readonly discS: string
  readonly priceS: string | null
  readonly rows: readonly TextRow[]
}

// «0,694q» for 0,89 kg: when no reading of the weight fits, the weight is paid ÷ price, if that
// comes out in whole grams — at a cost of two swaps, so a readable weight always wins.
function byWeight(budget: Budget, paidS: string, priceS: string): Candidate[] {
  const out: Candidate[] = []
  const paids = variants(budget.swaps, paidS)
  const prices = variants(budget.swaps, priceS)
  if (!afford(budget, paids.length * prices.length)) return out
  for (const p of paids) {
    for (const pr of prices) {
      const paid = hundredthsOf(p)
      const price = hundredthsOf(pr)
      const qty = Math.round((paid * 1000) / price)
      if (qty > 0 && Math.round((qty * price) / 1000) === paid) {
        out.push({
          qty,
          paid,
          disc: 0,
          price,
          swaps: 2 + diff(p, paidS) + diff(pr, priceS),
          rate: 0,
        })
      }
    }
  }
  return out
}

// Every way to cut the numbers into «paid» and «price»: «1 260 630» is 1 260 and 630, or 1 and
// 260 630; a count of pieces is what paid ÷ price makes whole.
function plainCandidates(budget: Budget, found: Found): Candidate[] {
  const tokens = found.plain ?? []
  const group = (g: readonly string[]): boolean =>
    g.length === 1 ||
    (/^\d{1,3}$/.test(g[0] ?? '') && g.slice(1).every((x) => /^\d{3}([.,]\d+)?$/.test(x)))
  const out: Candidate[] = []
  for (let k = 1; k < tokens.length; k++) {
    const a = tokens.slice(0, k)
    const b = tokens.slice(k)
    if (!group(a) || !group(b)) continue
    const paidS = a.join(' ')
    const priceS = b.join('')
    if (found.weighed)
      out.push(
        ...candidates(budget, found.qtyS, paidS, '0', priceS),
        ...byWeight(budget, paidS, priceS),
      )
    else
      for (let q = 1; q <= 20; q++)
        // the count is tried, not read: one the row does not print is worked out (MOL-255)
        out.push(
          ...candidates(budget, String(q), paidS, '0', priceS).map((x) =>
            x.qty === (found.count ?? 0) * 1000 ? x : { ...x, counted: true as const },
          ),
        )
  }
  // with no discount the paid sum is qty × price to the luma, not «about» it
  return out.filter((x) => Math.round((x.qty * x.price) / 1000) === x.paid)
}

const DATE = /(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/

function dateOf(text: string): { date: string | null; time: string | null } {
  const found = DATE.exec(text)
  const [, day, month, year, hour, minute] = found ?? []
  if (
    day === undefined ||
    month === undefined ||
    year === undefined ||
    hour === undefined ||
    minute === undefined
  ) {
    return { date: null, time: null }
  }
  return { date: `${year}-${month}-${day}`, time: `${hour}:${minute}` }
}

function cardReceipt(rows: readonly TextRow[], lx: Lexicon): Laid {
  const text = rows.map((r) => r.text).join('\n')
  const mapped = rows.map((r) => digits(r.text, lx))
  const found: Found[] = []
  let lastFigures = -1
  for (let i = 1; i < mapped.length; i++) {
    const row = mapped[i] ?? ''
    const figures = FIGURES.exec(row)
    const plain = figures === null ? PLAIN.exec(row) : null
    if (figures === null && plain === null) continue
    let j = i - 1
    while (j > 0 && (mapped[j] ?? '').trim() === '') j-- // OCR leaves blank lines between the halves
    const headRow = mapped[j] ?? ''
    const head = ITEM.exec(headRow)?.[2] ?? headRow
    // the name row is cut out only between two items: right below the figures of the one before
    // (review Р19) — above the first item is the head, whatever OCR reads at its edge
    let above = j - 1
    while (above > 0 && (mapped[above] ?? '').trim() === '') above--
    const betweenItems = above >= 0 && above === lastFigures
    const numbered = betweenItems && NUMBERED.test(headRow)
    const source = rows.filter((_, k) => (k === j && numbered) || k === i)
    lastFigures = i
    if (figures !== null) {
      const mid = figures[3] ?? ''
      const weight = WEIGHT.exec(mid)
      const weighed = weight !== null && !/^1[^\d,.]/.test(mid.trim())
      found.push({
        head,
        hs: figures[1] ?? '',
        sku: figures[2] ?? '',
        qtyS: weighed ? (weight[1] ?? '').replace('.', ',') : '1',
        weighed,
        count: null,
        plain: null,
        paidS: (figures[4] ?? '').trim(),
        discS: (figures[5] ?? '').replace(/\s/g, ''),
        priceS: figures[6]?.replace(/\s/g, '') ?? null,
        rows: source,
      })
    } else if (plain !== null) {
      const mid = plain[3] ?? ''
      const weight = WEIGHT.exec(mid)
      const weighed = weight !== null && !/^1[^\d,.]/.test(mid.trim())
      found.push({
        head,
        hs: plain[1] ?? '',
        sku: plain[2] ?? '',
        qtyS: weighed ? (weight[1] ?? '').replace('.', ',') : '1',
        weighed,
        count: Number(/^(\d{1,2})\D/.exec(mid.trim())?.[1] ?? Number.NaN) || null,
        plain: (plain[4] ?? '').trim().split(/\s+/),
        paidS: (plain[4] ?? '').trim(),
        discS: '0',
        priceS: null,
        rows: source,
      })
    }
  }

  const printedTotal = lx.cardTotal.exec(text)
  const total = printedTotal?.[1] === undefined ? null : hundredthsOf(printedTotal[1])
  const budget: Budget = {
    left: READING_COMBINATIONS_MAX,
    floors: FLOOR_COMBINATIONS_MAX,
    swaps: lx.swaps,
  }
  const lists = found.map((f) => {
    if (f.plain !== null) return plainCandidates(budget, f)
    const before = funds(budget)
    const out = candidates(budget, f.qtyS, f.paidS, f.discS, f.priceS)
    return out.length === 0 && funds(budget) < before
      ? sliced(budget, f.qtyS, f.paidS, f.discS, f.priceS)
      : out
  })
  const tin = /(\d{8})\b/.exec(lx.cardTin.exec(text)?.[0] ?? '')?.[1] ?? null
  // whether the reading by its own total took a slice (MOL-246): `bestReading` lets no such total vouch
  let tookSlice = false
  const judge = (byTotal: boolean): ReceiptText => {
    const { picks, balanced, blankSum, doubt, sliced } = judged(
      lists,
      byTotal ? total : null,
      found.map((f) => (f.plain !== null ? f.plain.join('') : f.paidS).replace(/\D/g, '')),
    )
    if (byTotal) tookSlice = sliced
    const lines = found.map((f, i): ReceiptTextLine => {
      const pick = picks[i] ?? null
      // what the total changed or could not tell apart: highlighted for the person to check
      const settled = pick !== null && !doubt.has(i)
      return {
        printed: f.head.trim(),
        hs: f.hs.padStart(4, '0'),
        sku: f.sku,
        quantityMilli: finite(pick?.qty ?? milliOf(f.qtyS)),
        unit: f.weighed ? 'kg' : 'piece',
        priceHundredths: finite(pick?.price ?? (f.priceS === null ? null : hundredthsOf(f.priceS))),
        sumHundredths: finite(pick?.paid ?? blankSum ?? hundredthsOf(f.paidS)),
        discountHundredths: finite(pick?.disc ?? hundredthsOf(f.discS)),
        settled,
        asPrinted: settled && pick.swaps === 0 && pick.counted !== true,
        // a row of two bare figures prints no price apart: which is which is the parse's choice
        ...(f.priceS === null
          ? {}
          : {
              read: {
                quantityMilli: finite(milliOf(f.qtyS)),
                priceHundredths: finite(hundredthsOf(f.priceS)),
                discountHundredths: finite(hundredthsOf(f.discS)),
                sumHundredths: finite(hundredthsOf(f.paidS)),
              },
            }),
        rows: f.rows,
      }
    })

    return {
      layout: 'card',
      tin,
      ...dateOf(text),
      receiptNo: lx.cardNo.exec(text)?.[1] ?? null,
      totalHundredths: total,
      readTotalHundredths: total,
      balanced,
      lines,
    }
  }
  const read = judge(true)
  return { read, asPrinted: () => judge(false), sliced: tookSlice }
}

// Every way to read space-separated digit groups as numbers: «2 200» is one number or two. Values
// are thousandths, so that «1,5» kg and «2 200» ֏ are both whole.
function splits(tokens: readonly string[]): number[][] {
  const out: number[][] = []
  const walk = (i: number, acc: number[]): void => {
    if (i === tokens.length) {
      out.push(acc)
      return
    }
    for (let j = i + 1; j <= tokens.length; j++) {
      const group = tokens.slice(i, j)
      if (group.length > 1 && !group.slice(1).every((g) => /^\d{3}([.,]\d+)?$/.test(g))) break
      if (group.length > 1 && !/^\d{1,3}$/.test(group[0] ?? '')) break
      walk(j, [...acc, milliOf(group.join(''))])
    }
  }
  walk(0, [])
  return out
}

interface TableOption {
  readonly qty: number | null
  readonly price: number | null
  readonly sum: number | null
  readonly settled: boolean
  /**
   * The row's last figure is its sum, as a till prints it: what the row reads with no total (MOL-244). Its
   * price is worked out, so the line it gives is never `asPrinted` (MOL-255).
   */
  readonly lastIsSum?: boolean
}

// Past this many combinations the total no longer chooses among the readings of a table: a long
// table would otherwise hold the queue for minutes. The bench's tables are far below it.
const TABLE_COMBINATIONS_MAX = 100_000

// The second layout family: a table «name | qty | price | sum» with the heading in brackets —
// «(3824) ՏՈՖՈՒ ՀՈՂ …» (Dog City). A row runs from one «(dddd)» to the next; the figures are the
// numbers at the ends of its lines, and how «1 2 200 2 200» splits into qty, price and sum is
// decided by qty × price = sum. A row whose figures OCR lost takes what the total leaves over.
function tableLines(
  rows: readonly TextRow[],
  lx: Lexicon,
): {
  total: number | null
  judge: (byTotal: boolean) => ReceiptTextLine[]
} {
  const text = rows.map((r) => r.text).join('\n')
  const items: { hs: string; words: string[]; rows: TextRow[] }[] = []
  for (const row of rows) {
    const mapped = digits(row.text, lx)
    const heading = /\((\d{4})\)\s*(.*)$/.exec(mapped)
    const last = items.at(-1)
    if (heading !== null)
      items.push({ hs: heading[1] ?? '', words: [heading[2] ?? ''], rows: [row] })
    else if (last !== undefined && !lx.tableEnd.test(mapped)) {
      last.words.push(mapped)
      last.rows.push(row)
    } else if (last !== undefined) break
  }
  const printedTotal = lx.tableTotal.exec(text)?.[1]
  const total = printedTotal === undefined ? null : hundredthsOf(printedTotal)

  const options = items.map((item) => {
    // a row number at the start of a line («| 1|», «ւ 2/») is not a figure
    const body = item.words
      .map((w, k) => (k > 0 ? w.replace(/^[^\p{L}\d]*\p{L}?\s*\d{1,2}\s*[|/[]/u, ' ') : w))
      .join(' ')
    const tokens = body.match(/(?<![\p{L}\d])\d+(?:[.,]\d+)?(?![\p{L}\d(])/gu) ?? []
    const tail = tokens.slice(-5)
    const out: TableOption[] = []
    for (let k = tail.length; k >= 1; k--) {
      for (const sp of splits(tail.slice(-k))) {
        const [a, b, c] = sp
        if (sp.length === 3 && a !== undefined && b !== undefined && c !== undefined) {
          // thousandths × thousandths against thousandths: within a luma, as the prototype allowed
          if (Math.abs(a * b - c * 1000) < 11_000) {
            out.push({ qty: a, price: Math.round(b / 10), sum: Math.round(c / 10), settled: true })
          }
        }
        if (sp.length === 2 && a !== undefined && b !== undefined && a > 0 && a < 100_000) {
          out.push({
            qty: a,
            price: Math.round(b / 10),
            sum: Math.round((a * b) / 10_000),
            settled: false,
          })
          out.push({
            qty: a,
            price: Math.round((b * 100) / a),
            sum: Math.round(b / 10),
            settled: false,
            lastIsSum: true,
          })
        }
      }
    }
    if (out.length === 0)
      out.push({ qty: milliOf(tail[0] ?? '1'), price: null, sum: null, settled: false })
    const name = body
      .replace(/[|[\]{}]/g, ' ')
      .replace(/(?:^|\s)\d+(?:[.,]\d+)?(?=\s|$)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    return { item, name, weighed: lx.tableWeighed.test(body), out: out.slice(0, 6) }
  })

  // The receipt's total decides among the readings of rows OCR left short (one row may be blank —
  // what it leaves is the review's difference, never the row's sum: MOL-255, В-5 «а»); among readings
  // that add up, the one with rounder prices wins — a shop prices in whole tens, 2 200 and 900, not 1 525.
  // With no total to judge — none read, or none shown (MOL-244) — a row reads as printed: its own arithmetic
  // first, else its last figure its sum — «1,5 1 350» is 1 350 at 900, never 2 025 at 1 350 (review 6, 7).
  const judge = (byTotal: boolean): ReceiptTextLine[] => {
    const judging = byTotal ? total : null
    let chosen: TableOption[] = options.map(
      (o) =>
        (judging === null
          ? (o.out.find((x) => x.settled) ?? o.out.find((x) => x.lastIsSum))
          : undefined) ??
        o.out[0] ?? { qty: null, price: null, sum: null, settled: false },
    )
    const round = (x: number | null): number => (x !== null && x % 1000 === 0 ? 1 : 0)
    let bestScore = -Infinity
    const pick = (i: number, acc: TableOption[]): void => {
      if (judging === null) return
      if (i === options.length) {
        const known = acc.filter((x) => x.sum !== null)
        const rest = judging - known.reduce((a, x) => a + (x.sum ?? 0), 0)
        const blanks = acc.length - known.length
        if (!((blanks === 0 && Math.abs(rest) <= 1) || (blanks === 1 && rest > 0))) return
        const score =
          -10 * blanks +
          acc.filter((x) => x.settled).length +
          acc.reduce((a, x) => a + round(x.price) + round(x.sum), 0) +
          (blanks > 0 ? round(rest) * 2 : 0)
        if (score > bestScore) {
          bestScore = score
          chosen = acc
        }
        return
      }
      for (const o of options[i]?.out ?? []) pick(i + 1, [...acc, o])
    }
    const combinations = options.reduce((n, o) => n * o.out.length, 1)
    if (judging !== null && combinations <= TABLE_COMBINATIONS_MAX) pick(0, [])

    const lines = options.map((o, i): ReceiptTextLine => {
      const c = chosen[i] ?? { qty: null, price: null, sum: null, settled: false }
      const { sum, price } = c
      return {
        printed: o.name,
        hs: o.item.hs,
        sku: null,
        quantityMilli: finite(c.qty),
        unit: o.weighed ? 'kg' : 'piece',
        priceHundredths: finite(price),
        sumHundredths: finite(sum),
        discountHundredths: null,
        settled: c.settled,
        // only the split of three figures settles, and it is all printed
        asPrinted: c.settled,
        rows: o.item.rows.slice(0, 1),
      }
    })
    return lines
  }
  return { total, judge }
}

function tableReceipt(
  rows: readonly TextRow[],
  total: number | null,
  lines: ReceiptTextLine[],
  lx: Lexicon,
): ReceiptText {
  const text = rows.map((r) => r.text).join('\n')
  return {
    layout: 'table',
    tin: lx.tableTin.exec(text)?.[1] ?? null,
    ...dateOf(text),
    receiptNo:
      lx.tableNo.exec(text)?.[1] ?? lx.tableReceiptNo.exec(text)?.[1]?.replace(/\s/g, '') ?? null,
    totalHundredths: total,
    readTotalHundredths: total,
    // the prototype's table reading never said whether its lines met the total
    balanced: false,
    lines,
  }
}

// The third reading (MOL-226). An Armenian fiscal till marks every item with its class code after
// «Դաս.»: a customs heading of four digits for goods, a class of services «dd.dd» for food service —
// often with its own article, «Ն/Կ 745030». Where the name and the figures stand differs by print:
// the till's table puts the code, the article and the figures on one row and the name under them;
// the fiscal terminal (ՀԴՄ) prints the code, the name, then «688.09x1.0 հատ=688.09դրամ» and no
// article. So the code is a boundary rather than a layout: a row of figures is an item, its code
// the last one above it, and its name on the side the receipt's coded items put it.

// The till's article after the class: «Ն/Կ 745030», read «ՆԿ», «ԽԿ» — after a digit or two of the code's
// own junk too where its mark is read, «56.10 19 Ն/Կ 745030» (review 5, № 15).
const CLASS_ARTICLE =
  /^(?:[^\d]{0,8}?|[^\d]{0,3}\d{1,2}[^\d]{0,3}(?:Ն\/?Կ|ՆԿ|Խ\/?Կ{1,2})[^\d]{0,3})(\d{5,7})(?!\d)/u
// The article's mark is no word of a name: «Դաս. 56.10, Ն/Կ 1հատ …» with its digits lost (№ 15).
const ARTICLE_MARK = /^(?:Ն\/?Կ|ՆԿ|Խ\/?Կ{1,2})$/u
// The terminal's figures: «price x qty unit = sum դրամ»; OCR reads «x» as «х» or «:».
const TERMINAL_TIMES = /(\d+(?:[.,]\d+| \d{2})?)\s*[xх×*:]\s*(\d+(?:[.,]\d+| \d)?)\s*(\S*)\s*$/u

interface ClassFigures {
  readonly qtyS: string | null
  readonly priceS: string | null
  readonly sumS: string
  readonly weighed: boolean
  // the terminal prints its sum with two decimals always, its count with one
  readonly terminal: boolean
  // the row's own letters: «16 Թև 1հատ 3931.91 3931.91» names its item on the same row
  readonly words: string
}

// «688 09» is 688.09 whose point OCR read as a space.
const pointed = (text: string): string => text.replace(/^(\d+) (\d{1,2})$/, '$1.$2')

// A figure whose decimal point OCR lost: «393191» for 3931.91, «10» for 1.0.
function unpointed(text: string, places: number): string[] {
  return /^\d+$/.test(text) && text.length > places
    ? [`${text.slice(0, -places)}.${text.slice(-places)}`]
    : []
}

// The words of a name on a row: a number before a word stays — a count «16 Թև», a volume «1000 մլ» —
// figures and units of the till go.
function wordsOf(text: string, lx: Lexicon): string {
  const tokens = text
    .replace(lx.tillWords, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}\d]+|[^\p{L}\d]+$/gu, ''))
    .filter((w) => !ARTICLE_MARK.test(w))
  const kept = tokens.filter(
    (w, i) =>
      (w.match(/\p{L}/gu) ?? []).length >= 2 ||
      // a number before a word of two letters, or before «լ», «գ»: not before a smudge, «2 Ա» (review 3)
      (/^\d{1,4}(?:[.,]\d+)?$/.test(w) &&
        ((tokens[i + 1]?.match(/\p{L}/gu) ?? []).length >= 2 ||
          lx.oneLetterUnit.test(tokens[i + 1] ?? ''))) ||
      (lx.oneLetterUnit.test(w) && /^\d/.test(tokens[i - 1] ?? '')),
  )
  return kept.join(' ')
}

// OCR reads smudges as Latin or Cyrillic («Ստրիպս Thuin ‘Al»): in a name with words of the country's
// letters, a word with none of them is dropped.
const nameOf = (parts: readonly string[], lx: Lexicon): string => {
  const words = parts
    .join(' ')
    .split(' ')
    .filter((w) => w !== '')
  const armenian = words.filter((w) => !/\p{L}/u.test(w) || lx.letter.test(w))
  return (armenian.some((w) => lx.letter.test(w)) ? armenian : words).join(' ')
}

function classFigures(row: string, lx: Lexicon): ClassFigures | null {
  const sum = lx.terminalSum.exec(row)
  if (sum?.[1] !== undefined) {
    const left = row.slice(0, sum.index).replace(lx.terminalUnit, '')
    const times = TERMINAL_TIMES.exec(left)
    return {
      qtyS: times?.[2] === undefined ? null : pointed(times[2]),
      priceS: times?.[1] === undefined ? null : pointed(times[1]),
      sumS: pointed(sum[1]),
      weighed: lx.kg.test(row.slice(0, sum.index)),
      terminal: true,
      words: wordsOf(row.slice(0, times?.index ?? sum.index), lx),
    }
  }
  const unit = lx.classUnit.exec(row)
  const tail = unit === null ? row : row.slice(unit.index + unit[0].length)
  const numbers = tail.match(/(?<![\p{L}\d])\d+(?:[.,]\d+)?(?![\p{L}\d])/gu) ?? []
  // with no unit read, a row of figures is one that ends in two amounts: «624.44 624.41»
  if (unit === null && !/\d[.,]?\d*\s+\d[\d.,]*\s*$/.test(row)) return null
  const sumS = numbers.at(-1)
  if (sumS === undefined) return null
  return {
    qtyS: unit?.[1] ?? null,
    priceS: numbers.length > 1 ? (numbers.at(-2) ?? null) : null,
    sumS,
    weighed: unit?.[2] !== undefined && lx.kgWhole.test(unit[2]),
    terminal: false,
    words: wordsOf(unit === null ? row.replace(/[\d\s.,]+$/, '') : row.slice(0, unit.index), lx),
  }
}

// Every reading of a line's figures that holds quantity × price = sum, as the card's are found; a
// figure whose point OCR lost costs one swap more. A line whose price nobody read takes the sum
// alone, which `guessed` marks: it says nothing of the line's arithmetic.
function classCandidates(budget: Budget, f: ClassFigures, guessed: Set<Candidate>): Candidate[] {
  // each figure as read, then with its point put back; the terminal's sum has its two decimals always
  const read = (text: string, places: number, free: boolean): { text: string; cost: number }[] => [
    ...(free && unpointed(text, places).length > 0 ? [] : [{ text, cost: 0 }]),
    ...unpointed(text, places).map((t) => ({ text: t, cost: free ? 0 : 1 })),
  ]
  const sums = read(f.sumS, 2, f.terminal)
  const prices = f.priceS === null ? [] : read(f.priceS, 2, false)
  const qtys =
    f.qtyS === null
      ? Array.from({ length: 20 }, (_, q) => ({ text: String(q + 1), cost: 0 }))
      : read(f.qtyS, f.weighed ? 3 : 1, false).filter((q) => f.terminal || q.cost === 0)
  const every = (find: typeof candidates): Candidate[] => {
    const found: Candidate[] = []
    for (const sum of sums) {
      for (const price of prices) {
        for (const qty of qtys) {
          for (const x of find(budget, qty.text, sum.text, '0', price.text)) {
            const swaps = x.swaps + sum.cost + price.cost + qty.cost
            // a count no unit was read with is one of twenty tried: worked out, never as printed (MOL-255)
            found.push(f.qtyS === null ? { ...x, swaps, counted: true } : { ...x, swaps })
          }
        }
      }
    }
    return found
  }
  const before = funds(budget)
  const out = every(candidates)
  // only where no reading holds, a sign read as a digit (MOL-246) — never beside a count not printed:
  // twenty counts made up would let it choose one (review Б1)
  if (out.length === 0 && f.qtyS !== null && funds(budget) < before) out.push(...every(sliced))
  // no price read, or none that fits: the sum alone, at the count read — a line, not its arithmetic
  const sum = sums[0]
  if (
    out.every((x) => x.sliced === true) &&
    sum !== undefined &&
    (f.priceS === null || f.terminal)
  ) {
    const qty = f.qtyS === null ? 1000 : milliOf(qtys[qtys.length - 1]?.text ?? f.qtyS)
    const paid = hundredthsOf(sum.text)
    if (Number.isFinite(paid) && paid > 0 && qty > 0) {
      const sole = { qty, paid, disc: 0, price: Math.round((paid * 1000) / qty), swaps: 2, rate: 0 }
      guessed.add(sole)
      out.push(sole)
    }
  }
  return out
}

// How far above the first code the figures of an item whose code row OCR lost are looked for.
const CLASS_LOOKBACK = 3

interface ClassAnchor {
  readonly hs: string
  readonly sku: string | null
  readonly rest: string
}

/** The rows as the class reading reads them, how it finds a code, and where its list begins. */
function classList(
  rows: readonly TextRow[],
  lx: Lexicon,
): {
  mapped: string[]
  anchorOf: (row: string) => ClassAnchor | null
  start: number
} {
  const mapped = rows.map((r) => r.text.replace(lx.digitLikeAt, (c) => lx.digitLike[c] ?? c))
  // a class of services read with its point anywhere on the receipt is the receipt's: «5610» and
  // «56 10» on the other rows are it, and so is a row whose «Դաս» OCR lost
  const services = new Set(
    mapped.flatMap((row) => {
      const mark = lx.classMark.exec(row)
      return mark !== null && /\d{2}[.,]\d{2}/.test(mark[0])
        ? [`${mark[1] ?? ''}${mark[2] ?? ''}`]
        : []
    }),
  )
  const anchorOf = (row: string): ClassAnchor | null => {
    let mark: RegExpExecArray | null = lx.classMark.exec(row)
    if (mark === null) {
      for (const service of services) {
        const loose = new RegExp(
          `^[^\\d]{0,12}?(${service.slice(0, 2)})\\s?[.,]?\\s?(${service.slice(2)})${lx.classTail}`,
          'u',
        ).exec(row)
        // with no «Դաս» the class is the code only where what follows is a code's (review 5, 6): nothing,
        // a digit or two of junk «ши‘ 56 10 19», an article after its comma or mark — junk before the
        // mark too, «ամ. 56.10 19 Ն/Կ 745030» — or a line's figures whose own arithmetic holds as read,
        // «ամ. 56.10 1հատ 688.09 688.09». A price that begins with the class is none of them, whatever
        // OCR made of the rest — «5610 5640», «5610:1.0», «5610 00:1.0», «5610 11220»
        if (loose !== null && looseCode(row.slice(loose.index + loose[0].length), lx)) mark = loose
      }
    }
    if (mark === null) return null
    const digits = `${mark[1] ?? ''}${mark[2] ?? ''}`
    const after = row.slice(mark.index + mark[0].length)
    const article = CLASS_ARTICLE.exec(after)
    return {
      hs: services.has(digits) ? `${digits.slice(0, 2)}.${digits.slice(2)}` : digits,
      sku: article?.[1] ?? null,
      rest: article === null ? after : after.slice(article.index + article[0].length),
    }
  }
  // The list begins at the first code — or at the item above it whose code row OCR lost whole (review
  // А2): its figures, with a unit or the terminal's sum, within three rows of names over the code.
  let start = mapped.findIndex((row) => anchorOf(row) !== null)
  // a till that prints its names in capitals, «ԹՎԻՍՏԵՐ»: a word of capitals over a dish is then its name,
  // not the head's (review 4, Г2)
  const capitals = mapped
    .slice(Math.max(start, 0))
    .some(
      (row) =>
        lx.capitals.test(row) &&
        !lx.small.test(row) &&
        !/\d/.test(row) &&
        !lx.classEnd.test(row) &&
        !lx.classHead.test(row),
    )
  for (let i = start - 1, seen = 0; start > 0 && i >= 0 && seen < CLASS_LOOKBACK; i--) {
    const row = mapped[i] ?? ''
    if (row.trim() === '') continue
    seen += 1
    const figures = classFigures(row, lx)
    if (figures !== null && (figures.terminal || lx.classUnit.test(row))) {
      start = i
      // the terminal prints the name over its figures: the name rows right above them are the item's,
      // or the dish is left nameless and its name is read as the head (review 2, Б1) — up to three rows
      // with no digit but a count before the name, and none of the head (review 3, В4): a word of
      // capitals «ԳՅՈՒՄՐԻ», «ՖԻՍԿԱԼ» on a till that names in small letters, or the cashier's «Գանձապահ»
      // — a name of nobody's goes nowhere. A row over the first is the name's only where the name runs
      // on across the rows, one of the two starting small: «դե լյուքս», «նալ» — never «Շնորհակալություն»
      // over «Պանրային սոուս» (review 4, № 14)
      let below: string | null = null
      for (let j = i - 1, names = 0; j >= 0 && names < CLASS_LOOKBACK; j--) {
        const above = mapped[j] ?? ''
        if (above.trim() === '') continue
        if (
          wordsOf(above, lx) === '' ||
          /\d/.test(above.replace(/^\s*\d{1,3}\s+(?=\p{L})/u, '')) ||
          (!capitals && lx.capitals.test(above)) ||
          lx.classHead.test(above) ||
          (below !== null && !lx.startsSmall.test(above) && !lx.startsSmall.test(below))
        ) {
          break
        }
        start = j
        names += 1
        below = above
      }
      break
    }
  }
  return { mapped, anchorOf, start }
}

// What may follow a class read with no «Դաս» for it to be the code (see `classList`).
function looseCode(rest: string, lx: Lexicon): boolean {
  if (!/\d/.test(rest) || /^[^\d]*\d{1,2}(?!\d)[^\d]*$/.test(rest)) return true
  const article = CLASS_ARTICLE.exec(rest)
  if (article?.[1] !== undefined && /[,\p{L}]/u.test(rest.slice(0, rest.indexOf(article[1]))))
    return true
  const figures = classFigures(rest, lx)
  if (figures?.priceS == null || !(figures.terminal || lx.classUnit.test(rest))) {
    return false
  }
  const budget: Budget = { left: LINE_COMBINATIONS_FLOOR, floors: 0, swaps: lx.swaps }
  return classCandidates(budget, figures, new Set()).some((x) => x.swaps === 0)
}

/** Where a fiscal till's list of items begins: its first class code, or -1 (MOL-226). */
export function classListStart(reading: PhotoReading, rows: readonly TextRow[]): number {
  return classList(rows, lexiconOf(reading)).start
}

function classReceipt(rows: readonly TextRow[], lx: Lexicon): Laid {
  const text = rows.map((r) => r.text).join('\n')
  const { mapped, anchorOf, start: first } = classList(rows, lx)
  const end = first < 0 ? -1 : mapped.findIndex((row, i) => i > first && lx.classEnd.test(row))
  const list = first < 0 ? [] : mapped.slice(first, end < 0 ? undefined : end)
  type Kind =
    | { kind: 'anchor'; hs: string; sku: string | null; figures: ClassFigures | null }
    | { kind: 'figures'; figures: ClassFigures }
    | { kind: 'name'; words: string }
    | { kind: 'other' }
  const kinds = list.map((row): Kind => {
    const anchor = anchorOf(row)
    if (anchor !== null)
      return { kind: 'anchor', ...anchor, figures: classFigures(anchor.rest, lx) }
    const figures = classFigures(row, lx)
    if (figures !== null) return { kind: 'figures', figures }
    const words = wordsOf(row, lx)
    // an article or a code is no name: «Ан Ц 56. յ wit 70211»
    return words !== '' && !/\d{5}/.test(row) ? { kind: 'name', words } : { kind: 'other' }
  })

  interface Item {
    readonly at: number
    readonly hs: string | null
    readonly sku: string | null
    readonly figures: ClassFigures
    readonly anchored: number | null
  }
  const items: Item[] = []
  let anchor: { at: number; hs: string; sku: string | null } | null = null
  for (const [at, k] of kinds.entries()) {
    if (k.kind === 'anchor') anchor = { at, hs: k.hs, sku: k.sku }
    const figures = k.kind === 'anchor' ? k.figures : k.kind === 'figures' ? k.figures : null
    if (figures === null) continue
    items.push({
      at,
      hs: anchor?.hs ?? null,
      sku: anchor?.sku ?? null,
      figures,
      anchored: anchor?.at ?? null,
    })
    anchor = null
  }
  // a line whose code OCR lost is of the receipt's class where every code read is one class of
  // services: a dish of KFC is no product for the matcher because one row of it was smudged (А1b)
  const classes = new Set(items.flatMap((item) => (item.hs === null ? [] : [item.hs])))
  const [sole] = classes
  const lostHs = classes.size === 1 && sole?.includes('.') ? sole : null
  const namesBetween = (from: number, to: number): string[] =>
    kinds.slice(from, to).flatMap((k) => (k.kind === 'name' ? [k.words] : []))
  // the side the names stand on: the terminal's between the code and the figures, the till's under
  const above = items.filter(
    (item) =>
      item.anchored !== null &&
      item.anchored < item.at &&
      namesBetween(item.anchored + 1, item.at).length > 0,
  ).length
  const nameAbove = above * 2 > items.filter((item) => item.anchored !== null).length
  const names = items.map((item, i) => {
    const own = lx.letter.test(item.figures.words) ? [item.figures.words] : []
    if (nameAbove) {
      const from = item.anchored ?? items[i - 1]?.at ?? -1
      return [...namesBetween(from + 1, item.at), ...own]
    }
    const next = items[i + 1]?.at ?? kinds.length
    const below: string[] = []
    for (const k of kinds.slice(item.at + 1, next)) {
      if (k.kind !== 'name' || below.length === 2) break
      below.push(k.words)
    }
    return [...own, ...below]
  })

  const printedTotal = lx.classTotal.exec(text)?.[1]
  const total = printedTotal === undefined ? null : hundredthsOf(printedTotal)
  const budget: Budget = {
    left: READING_COMBINATIONS_MAX,
    floors: FLOOR_COMBINATIONS_MAX,
    swaps: lx.swaps,
  }
  const guessed = new Set<Candidate>()
  const lists = items.map((item) => classCandidates(budget, item.figures, guessed))
  // whether the reading by its own total took a slice (MOL-246): `bestReading` lets no such total vouch
  let tookSlice = false
  const judge = (byTotal: boolean): ReceiptText => {
    const { picks, balanced, blankSum, doubt, sliced } = judged(
      lists,
      byTotal ? total : null,
      items.map((item) => item.figures.sumS.replace(/\D/g, '')),
    )
    if (byTotal) tookSlice = sliced
    const lines = items.map((item, i): ReceiptTextLine => {
      const pick = picks[i] ?? null
      const f = item.figures
      const sumAsRead = f.terminal ? (unpointed(f.sumS, 2)[0] ?? f.sumS) : f.sumS
      const settled = pick !== null && !guessed.has(pick) && !doubt.has(i)
      return {
        printed: nameOf(names[i] ?? [], lx),
        hs: item.hs ?? lostHs,
        sku: item.sku,
        quantityMilli: finite(pick?.qty ?? (f.qtyS === null ? 1000 : milliOf(f.qtyS))),
        unit: f.weighed ? 'kg' : 'piece',
        priceHundredths: finite(pick?.price ?? (f.priceS === null ? null : hundredthsOf(f.priceS))),
        sumHundredths: finite(pick?.paid ?? blankSum ?? hundredthsOf(sumAsRead)),
        discountHundredths: 0,
        settled,
        asPrinted: settled && pick.swaps === 0 && pick.counted !== true,
        read: {
          quantityMilli: f.qtyS === null ? null : finite(milliOf(f.qtyS)),
          priceHundredths: f.priceS === null ? null : finite(hundredthsOf(f.priceS)),
          discountHundredths: null,
          sumHundredths: finite(hundredthsOf(sumAsRead)),
        },
        rows: [rows[first + item.at]].filter((row): row is TextRow => row !== undefined),
      }
    })
    return {
      layout: 'class',
      tin: lx.classTin.exec(text)?.[1] ?? null,
      ...dateOf(text),
      receiptNo: lx.classNo.exec(text)?.[1] ?? null,
      totalHundredths: total,
      readTotalHundredths: total,
      balanced,
      lines,
    }
  }
  const read = judge(true)
  return { read, asPrinted: () => judge(false), sliced: tookSlice }
}

// MOL-227 — a sole trader's terminal prints no items at all: its section «Բաժին 1», the turnover tax,
// the total, the payment, the tax number, the day. Read in full, it is no receipt to shoot again but a
// sum to record (MOL-78). Its head is the terminal's own, read here only: the three layouts keep theirs.
//
//   ՀՎՀՀ 57311783 Գ/Հ 31028805      ← the tax number before the till's registration number
//   ԿՀ: 00000049                     ← the receipt's number, never a tax number
//   04-10-26 16:30:59                ← DD-MM-YY
//   Բաժին 1 - Բաժին 1
//   / Շրջանառության հարկ/ 1700.00    ← the section's sum
//   Ընդամենը՝ 1700.00                ← the total
//   Առձեռն 1700.00                   ← paid in cash
//   ՖԻՍԿԱԼ ՀԱՄԱՐ 16013344

// «Բաժին 1», as OCR reads it: «Ււսժին 1]», «Բայժին 11» — the section and its number.
export const DEPARTMENT = /(?:Բա|Բայ|Ււս)ժին\s*\d/u
// The tax number: after its word first, anywhere; else before the till's registration number on the same
// row, however OCR read the word, «CUCC: 57311783 9/С: 31028805» — never the receipt's own number
// «ԿՀ: 00000049» on the row above it (review 1, № 1).
const DEPARTMENT_TIN_TILL = /(?<!\d)(\d{8})\s+\S{1,3}\/\S{1,3}:?\s*\d{6,}/u
const DEPARTMENT_DATE = /(?<!\d)(\d{2})[-.]\s?(\d{2})[-.]\s?(\d{2})\s+(\d{2}):(\d{2})/g
// the same, to ask of one row (`test` of a global pattern carries its place from row to row)
const DEPARTMENT_MOMENT = new RegExp(DEPARTMENT_DATE.source)
// The total's row as OCR reads its word, «Ընդամենը», «/չդամենը», «Կղդամեկը» — and not its discount,
// «Ընդամենը զեղչ», however OCR spells the discount's first letter.
// a sum since the start of the year is what a card bought this year, never the receipt's (MOL-288)
const totalRow = (text: string, lx: Lexicon): boolean =>
  lx.totalKept.test(text) && !lx.discountKept.test(text) && !lx.sinceStart.test(text)
// An amount with its hundredths: «1700.00», «1800 00», and with its thousands apart before a point,
// «2 050,01» — the card's payment, which read digit by digit was 50,01 in two places at once (MOL-244). A
// figure that lost its point is no vote.
const AMOUNT =
  /(?<![\d.,])(\d{1,3}(?: \d{3})+(?=[.,]\d{2}(?![\d.,]))|\d{1,7})[., ](\d{2})(?![\d.,])/g

const amountsOf = (text: string): number[] =>
  [...text.matchAll(AMOUNT)]
    .map((m) => Number(m[1]?.replace(/ /g, '')) * 100 + Number(m[2]))
    .filter((a) => a > 0)

/**
 * The amounts of a reading by where they stand: the total, the payment and, on a receipt with no items,
 * the section's sum — each a place of its own on the paper, read on its own by OCR.
 */
function placedAmounts(
  rows: readonly TextRow[],
  withSection: boolean,
  lx: Lexicon,
): Map<string, Set<number>> {
  const texts = rows.map((r) => r.text).filter((t) => t.trim() !== '')
  const found = new Map<string, Set<number>>([
    ['section', new Set()],
    ['total', new Set()],
    ['paid', new Set()],
  ])
  const add = (source: string, amounts: number[]): void => {
    for (const amount of amounts) found.get(source)?.add(amount)
  }
  // a row is one place, whatever words OCR put on it: «Ընդամենը Վճարված 2060.01» read twice is one vote,
  // and a row the section took as its tax is the section's alone (adversarial Б2)
  const taken = new Set<number>()
  texts.forEach((text, i) => {
    if (taken.has(i)) return
    if (withSection && DEPARTMENT.test(text)) {
      add('section', amountsOf(text))
      // the turnover tax under it carries the section's sum
      for (const [k, next] of texts.slice(i + 1, i + 3).entries()) {
        if (!lx.sectionTax.test(next)) continue
        add('section', amountsOf(next))
        taken.add(i + 1 + k)
      }
    } else if (totalRow(text, lx)) add('total', amountsOf(text))
    else if (lx.paid.test(text)) add('paid', amountsOf(text))
  })
  return found
}

/**
 * How the receipt was paid, by the words of its payment (MOL-312): one way only — a word of cash or of a card
 * beside a sum above nothing, and no word of the other so. A receipt that says both, a mixed payment, or
 * neither says nothing: an account suggested wrong is one the person may not look at (MOL-255). A redemption
 * of the card's bonus is no payment, «Կանխավճարի մարում» — the model has copied it «Կանխիկ վճարի մարում».
 */
export function paidByOf(
  reading: PhotoReading,
  places: readonly { readonly text: string; readonly amounts: readonly number[] }[],
): ReceiptPaidBy | null {
  const lx = lexiconOf(reading)
  const ways = new Set<ReceiptPaidBy>()
  for (const { text, amounts } of places) {
    if (!amounts.some((amount) => amount > 0) || lx.redeemed.test(text)) continue
    if (lx.cashless.test(text)) ways.add('card')
    else if (lx.cash.test(text)) ways.add('cash')
  }
  return ways.size === 1 ? ([...ways][0] ?? null) : null
}

/**
 * How Tesseract's readings say the receipt was paid (MOL-312): the rows from the total down, each reading's —
 * the payment is printed under the total, and a word above it is an item's («ձեռնոց», gloves). A reading that
 * lost the total's word says nothing.
 */
export function receiptPaidBy(
  reading: PhotoReading,
  readings: readonly (readonly TextRow[])[],
): ReceiptPaidBy | null {
  const lx = lexiconOf(reading)
  const places = readings.flatMap((rows) => {
    const texts = rowTexts(rows)
    const from = texts.findIndex((text) => totalRow(text, lx) || DEPARTMENT.test(text))
    return from < 0 ? [] : texts.slice(from).map((text) => ({ text, amounts: amountsOf(text) }))
  })
  return paidByOf(reading, places)
}

/**
 * The total two places on the paper vouch for, across the readings (MOL-227, MOL-244): the amount read in
 * the most of them, at least two, and the only one so — or null. One place read alike by both readings is
 * one vote: they read one photo, and a digit of this font misread is misread in both («2060.01» for
 * «2050.01»).
 */
function votedTotal(
  readings: readonly (readonly TextRow[])[],
  withSection: boolean,
  lx: Lexicon,
): number | null {
  const votes = new Map<number, Set<string>>()
  for (const rows of readings) {
    for (const [source, amounts] of placedAmounts(rows, withSection, lx)) {
      for (const amount of amounts) votes.set(amount, (votes.get(amount) ?? new Set()).add(source))
    }
  }
  const backed = [...votes].filter(([, sources]) => sources.size >= 2)
  const most = Math.max(0, ...backed.map(([, sources]) => sources.size))
  const totals = backed.filter(([, sources]) => sources.size === most)
  return totals.length === 1 ? (totals[0]?.[0] ?? null) : null
}

/**
 * A day of the calendar and a time a clock shows, or null: «64-10-26» or «76:36» is no reading at all. A year of
 * two digits is of this century.
 */
function calendarDayOf(year: string, month: string, day: string): string | null {
  const iso = `${year.length === 2 ? '20' : ''}${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  const date = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso ? null : iso
}

// where the day and the month stand among a date's first two parts, by the card's order
const DAY_AND_MONTH: Readonly<Record<DateOrder, readonly [day: 1 | 2, month: 1 | 2]>> = {
  'day-first': [1, 2],
}

/**
 * The day a receipt prints, read as its country's tills print it (MOL-305): a year of four digits first,
 * «2026-10-23», which one order alone reads — what the model is asked for — or the parts in the card's order, a year
 * of two digits or four last, «23-10-26», «04.10.2026», where it copied them as printed. Anything else, or no day of
 * the calendar, is none. Which order the model put a short year's parts in is not known here: the window of its arrival
 * (`receiptDateWindow`) is what refuses a day swapped with a year.
 */
export function printedDayOf(printed: string | null, reading: PhotoReading): string | null {
  const text = printed?.trim() ?? ''
  const yearFirst = /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/u.exec(text)
  if (yearFirst !== null)
    return calendarDayOf(yearFirst[1] ?? '', yearFirst[2] ?? '', yearFirst[3] ?? '')
  const parts = /^(\d{1,2})[-./]\s?(\d{1,2})[-./]\s?(\d{4}|\d{2})$/u.exec(text)
  if (parts === null) return null
  const [day, month] = DAY_AND_MONTH[reading.dateOrder]
  return calendarDayOf(parts[3] ?? '', parts[month] ?? '', parts[day] ?? '')
}
const clockOf = (hour: string, minute: string): string | null =>
  Number(hour) < 24 && Number(minute) < 60 ? `${hour}:${minute}` : null

/** The rows of a reading, one per line of text — a part's rows are lines already. */
const rowTexts = (rows: readonly TextRow[]): string[] => rows.map((row) => row.text)

/** What both readings say, or what the one that read it says; two answers are none. */
function agreed<T>(values: readonly (T | null)[]): T | null {
  const read = [...new Set(values.filter((v): v is T => v !== null))]
  return read.length === 1 ? (read[0] ?? null) : null
}

/**
 * A receipt of a till's section with no items (MOL-227): no reading found a line, a section «Բաժին» is
 * printed, and nothing an item prints is anywhere — or null. Its total is the amount at least two of
 * the section's sum, the total and the payment were read as, across the readings: a wrong sum on a trip
 * is worse than none, which the person sees and types (Р-3). A field the two readings read apart is
 * not read.
 */
export function departmentReceipt(
  reading: PhotoReading,
  readings: readonly (readonly TextRow[])[],
): ReceiptText | null {
  const lx = lexiconOf(reading)
  if (!readings.some((rows) => rows.some((row) => DEPARTMENT.test(row.text)))) return null
  if (readings.some((rows) => rows.some((row) => lx.itemMark.test(row.text)))) return null
  const texts = readings.map(rowTexts)
  const moments = texts.map((rows) => [
    ...new Map(
      rows
        .flatMap((text) => [...text.matchAll(DEPARTMENT_DATE)])
        .map(([found, day = '', month = '', year = '', hour = '', minute = '']) => [
          found,
          { date: calendarDayOf(year, month, day), time: clockOf(hour, minute) },
        ]),
    ).values(),
  ])
  // A receipt prints its moment, its total and its fiscal number once: two of any of them is two
  // receipts on one photo, whose total, time and number nobody can tell — the person holds the
  // receipts and types the total (am-21 of the bench). The moments alone missed a pair whose upper head
  // was out of the frame: its total and time came from the lower receipt, its number from the upper
  // one, and the upper receipt shot on its own was then refused as recorded (adversarial А1). And the
  // fiscal number is a receipt's last row: a moment or a tax number under it is the next receipt's head —
  // the middle of a tape of two, one of each in view, the time of the lower under the number of the
  // upper (round 2, Б1).
  const repeated = (rows: readonly string[], test: (text: string) => boolean): boolean =>
    rows.filter(test).length > 1
  const fiscal = (text: string): boolean => lx.fiscal.test(text)
  const headUnder = (rows: readonly string[]): boolean => {
    const end = rows.findIndex(fiscal)
    return (
      end >= 0 &&
      rows
        .slice(end + 1)
        .some(
          (text) =>
            DEPARTMENT_MOMENT.test(text) || lx.tinWord.test(text) || DEPARTMENT_TIN_TILL.test(text),
        )
    )
  }
  const several = texts.some(
    (rows, i) =>
      (moments[i]?.length ?? 0) > 1 ||
      repeated(rows, fiscal) ||
      repeated(rows, (text) => totalRow(text, lx)) ||
      headUnder(rows),
  )
  // a day or a time no calendar or clock has is no reading of it, and outvotes nothing (adversarial А3)
  const date = agreed(moments.flatMap((m) => m.map((moment) => moment.date)))
  const time = several ? null : agreed(moments.map((m) => m[0]?.time ?? null))
  const firstOf = (rows: readonly string[], pattern: RegExp): string | null =>
    rows.map((text) => pattern.exec(text)?.[1]).find((found) => found !== undefined) ?? null
  // the tax number of every head a reading holds, row by row
  const tins = texts.map((rows) =>
    rows.flatMap((text) => {
      const found = lx.tinWord.exec(text)?.[1] ?? DEPARTMENT_TIN_TILL.exec(text)?.[1]
      return found === undefined ? [] : [found]
    }),
  )
  // Two receipts on one photo: the tax number is the key of a place for everyone (review 1, № 1), so it
  // is read only where a reading holds both heads and they print one number — two receipts of one trader,
  // am-21. One head in view may be the other receipt's, another trader's: the upper receipt recorded at
  // its shop under the lower trader's number proposed that shop to everyone with his (round 4, Г1).
  const tin = several
    ? tins.some((found) => found.length > 1) && new Set(tins.flat()).size === 1
      ? (tins.flat()[0] ?? null)
      : null
    : agreed(texts.map((rows) => firstOf(rows, lx.tinWord) ?? firstOf(rows, DEPARTMENT_TIN_TILL)))
  return {
    layout: 'department',
    tin,
    date,
    time,
    // the number is the receipt's key against a second record (Т-11): read only where one reading holds
    // the receipt whole, its moment above its number — two readings each holding half a tape of two gave
    // the lower receipt's time under the upper one's number, and the upper one shot alone was refused as
    // recorded (round 3, В1). A number nobody vouches for so is the price of Р-8: two records, never none
    receiptNo: several
      ? null
      : agreed(
          texts.map((rows) => {
            const end = rows.findIndex(fiscal)
            const moment = rows.findIndex((text) => DEPARTMENT_MOMENT.test(text))
            return moment >= 0 && moment < end ? firstOf(rows, lx.fiscalNo) : null
          }),
        ),
    totalHundredths: several ? null : votedTotal(readings, true, lx),
    readTotalHundredths: null,
    balanced: false,
    lines: [],
  }
}

/** One reading of a receipt — the rows of its parts, joined — into lines with figures. */
export function parseReceiptText(reading: PhotoReading, rows: readonly TextRow[]): ReceiptText {
  const lx = lexiconOf(reading)
  return withTwins(readingOf(rows, lx).read, lx)
}

/**
 * An item read twice at a seam, its article one swap of OCR off, is a line of its own the total cannot
 * see (review Р27): two lines of one sum whose articles are so close are both a look away. Marked after
 * the reading is chosen, never in the choice: it says nothing of how well a reading read (am-13 on the
 * bench lost four right lines to a reading chosen so).
 */
function withTwins(read: ReceiptText, lx: Lexicon): ReceiptText {
  // only across a seam: in one photo an item has nowhere to be read twice, and two flavours of one
  // price numbered …5 and …6 are two items read right (review Р28)
  const parts = (line: ReceiptTextLine) => new Set(line.rows.map((row) => row.part))
  const apart = (a: ReceiptTextLine, b: ReceiptTextLine) => {
    const theirs = parts(b)
    return [...parts(a)].some((part) => !theirs.has(part))
  }
  const twin = (line: ReceiptTextLine) =>
    read.lines.some(
      (other) =>
        other !== line &&
        apart(line, other) &&
        line.sku !== null &&
        other.sku !== null &&
        line.sku !== other.sku &&
        near(line.sku, other.sku, lx.swaps) &&
        line.sumHundredths !== null &&
        other.sumHundredths === line.sumHundredths,
    )
  if (!read.lines.some(twin)) return read
  return {
    ...read,
    lines: read.lines.map((line) =>
      twin(line) ? { ...line, settled: false, asPrinted: false } : line,
    ),
  }
}

/**
 * One reading laid out: `read` — its lines judged by its own printed total; `asPrinted` — the same lines as
 * printed, for a total `bestReading` does not show by them (MOL-244). The candidates of a line's figures are
 * found once, so reading again costs the judging alone: the parse's ceilings hold.
 */
interface Laid {
  readonly read: ReceiptText
  readonly asPrinted: () => ReceiptText
  /** `read` met its total through a slice (MOL-246): the total's own choice, which vouches for nothing. */
  readonly sliced: boolean
}

function readingOf(rows: readonly TextRow[], lx: Lexicon): Laid {
  const card = cardReceipt(rows, lx)
  const text = rows.map((r) => r.text).join('\n')
  let laid = card
  // a customs heading names its item after it, «(3824) ՏՈՖՈՒՀՈՂ»; «(0312) 5-12-34» is a phone in the head,
  // and read as Dog City's table it made one «line» of the whole head (MOL-227, adversarial А2)
  if (/\(\d{4}\)\s*\p{L}/u.test(text)) {
    const table = tableLines(rows, lx)
    const lines = table.judge(true)
    if (lines.length > card.read.lines.length) {
      laid = {
        read: tableReceipt(rows, table.total, lines, lx),
        asPrinted: () => tableReceipt(rows, table.total, table.judge(false), lx),
        sliced: false,
      }
    }
  }
  // the class code reads a till neither layout knows; where one of them reads, it keeps the receipt
  const coded = classReceipt(rows, lx)
  return coded.read.lines.length > laid.read.lines.length ? coded : laid
}

/** The rows of a part's text, numbered as the reader numbered them. */
export function rowsOf(text: string, part: number): TextRow[] {
  return text.split('\n').map((line, index) => ({ text: line, part, line: index }))
}

// A long receipt comes in parts shot with an overlap (MOL-124 В-1). The seam is found by the till's
// articles: the last article of the text so far that the next part also has (exactly, else with one
// digit off — OCR), and the next part is taken from the row after it. A seam must be one an overlap
// can make (review Р7–Р9): the next part starts inside the text so far, so it holds no more articles
// before the seam than the text so far does; and what the text so far holds after the seam is in the
// next part after it too. Another item's near article, or the first of two bags, makes no such seam.
// No seam — an overlap of a name row only — joins the parts, dropping the rows both of them hold.
const ARTICLE = /\d{4}\s*\/\s*(\d{5,})/

/**
 * Two figures at most `depth` digits of `swaps` apart — a reader's confusions, Tesseract's or the model's — not the
 * same figure.
 */
export function swapApart(
  swaps: Readonly<Record<string, string>>,
  a: string,
  b: string,
  depth = 1,
): boolean {
  if (a === b || a.length !== b.length) return false
  let off = 0
  for (let i = 0; i < a.length; i++) {
    if (a.charAt(i) === b.charAt(i)) continue
    off += 1
    if (off > depth || swaps[a.charAt(i)] !== b.charAt(i)) return false
  }
  return true
}

// One digit off the way OCR misreads this font (5 for 6, 1 for 4, 3 for 8, 0 for 9) — not any
// digit: a till numbers one maker's line in a row, and 1160033 and 1160036 are two flavours of one
// yoghurt, not one article read twice (review Р23).
function near(a: string, b: string, swaps: Readonly<Record<string, string>>): boolean {
  if (a.length !== b.length) return false
  let off = -1
  for (let i = 0; i < a.length; i++) {
    if (a.charAt(i) === b.charAt(i)) continue
    if (off >= 0) return false
    off = i
  }
  return off < 0 || swaps[a.charAt(off)] === b.charAt(off)
}
const articleOf = (row: TextRow): string => ARTICLE.exec(row.text)?.[1] ?? ''

interface Article {
  readonly article: string
  readonly at: number
}

function articlesOf(rows: readonly TextRow[]): Article[] {
  return rows.flatMap((row, at) => {
    const article = articleOf(row)
    return article === '' ? [] : [{ article, at }]
  })
}

// How alike two rows read: 1 for the same text, 0 for nothing in common (Levenshtein over the longer).
// Only the start of a row counts (review Р22): a row of a thousand letters — a picture read as text —
// made one seam cost seconds, and a till's row is under a hundred.
const LIKENESS_CHARS = 80

export function likeness(a: string, b: string): number {
  const x = a.replace(/\s+/g, '').slice(0, LIKENESS_CHARS)
  const y = b.replace(/\s+/g, '').slice(0, LIKENESS_CHARS)
  if (x === '' || y === '') return 0
  let previous = Array.from({ length: y.length + 1 }, (_, k) => k)
  for (let i = 1; i <= x.length; i++) {
    const current = [i]
    for (let k = 1; k <= y.length; k++) {
      current[k] = Math.min(
        (previous[k] ?? 0) + 1,
        (current[k - 1] ?? 0) + 1,
        (previous[k - 1] ?? 0) + (x.charAt(i - 1) === y.charAt(k - 1) ? 0 : 1),
      )
    }
    previous = current
  }
  return 1 - (previous[y.length] ?? 0) / Math.max(x.length, y.length)
}

// The rows of an overlap read twice look alike even where OCR read one of them worse.
const ALIKE = 0.7

const NUMBER = /^\s*(\d{1,2})(?!\d)\s*[.,]?\s*\p{L}/u

/**
 * Two rows the overlap could hold twice: read alike, and not two items — not two numbers of the list,
 * not two articles (review Р23, Р25). Two flavours of one yoghurt read alike letter for letter,
 * «8.Յոգուրտ … դեղձ» and «9.Յոգուրտ … ելակ», and their articles may be one swap of OCR apart,
 * 1160035 and 1160036: an article read wrong is told by the name above it, never by its digits.
 */
function sameRow(a: string, b: string): boolean {
  const [na, nb] = [NUMBER.exec(a)?.[1], NUMBER.exec(b)?.[1]]
  if (na !== undefined && nb !== undefined && na !== nb) return false
  const [aa, ab] = [ARTICLE.exec(a)?.[1], ARTICLE.exec(b)?.[1]]
  if (aa !== undefined && ab !== undefined && aa !== ab) return false
  return likeness(a, b) >= ALIKE
}

// Two articles one could be the other misread: two digits off at most, each by a swap OCR makes.
function misread(a: string, b: string, swaps: Readonly<Record<string, string>>): boolean {
  if (a.length !== b.length) return false
  const off = Array.from(a).flatMap((c, i) => (c === b.charAt(i) ? [] : [[c, b.charAt(i)]]))
  return off.length <= 2 && off.every(([x, y]) => swaps[x ?? ''] === y)
}

// The name above an article, as one row and as two — OCR splits a long one (review Р21), and its upper
// row then starts with the item's number: none where the row above is blank, another article or not
// there. A cut row of figures above a name is not its first half (review Р25).
function namesAbove(rows: readonly TextRow[], at: number): string[] {
  const isName = (text: string) => text.trim() !== '' && !ARTICLE.test(text)
  const [one, two] = [rows[at - 1]?.text ?? '', rows[at - 2]?.text ?? '']
  if (!isName(one)) return []
  return isName(two) && NUMBER.test(two) && !NUMBER.test(one) ? [one, `${two} ${one}`] : [one]
}

/**
 * Two articles misread one for the other stand under one item's name. Neighbours of one maker's line
 * are numbered in a row, and one pair in ten of them — 1160035 and 1160036 — differs by a swap OCR
 * makes (review Р25): the digits alone cannot tell them, the names can. A name on one side only is no
 * agreement: the top edge of a part cuts between an item's name and its figures as often as anywhere,
 * and the strawberry yoghurt was lost there (review Р26). An item read twice with no name to agree is
 * left to show as twice, unbalanced, rather than an item lost behind a balanced total. Only a till that
 * prints no name above its articles at all leaves the digits to decide.
 */
function namesAgree(rows: readonly TextRow[], at: number, next: readonly TextRow[], to: number) {
  const [mine, theirs] = [namesAbove(rows, at), namesAbove(next, to)]
  if (mine.length === 0 && theirs.length === 0) return true
  return mine.some((x) => theirs.some((y) => sameRow(x, y)))
}

/** How many rows of either part an overlap is looked for in: an overlap is a few items, not a page. */
const OVERLAP_ROWS = 16
/**
 * Rows at the next part's top edge that may precede the overlap, and at the first part's foot that may
 * follow it: a cut row, a blank, a smudge.
 */
const OVERLAP_LEAD = 3

interface Seam {
  readonly mine: number
  readonly theirs: number
}

/** The strict seam: by the till's articles, as above; one swap of OCR off only under one name. */
function articleSeam(
  rows: readonly TextRow[],
  next: readonly TextRow[],
  exactOnly: boolean,
  swaps: Readonly<Record<string, string>>,
): Seam | null {
  const mine = articlesOf(rows)
  const theirs = articlesOf(next)
  const twin = (a: Article, b: Article) =>
    a.article === b.article ||
    (near(a.article, b.article, swaps) && namesAgree(rows, a.at, next, b.at))
  for (let i = mine.length - 1; i >= 0; i--) {
    const a = mine[i]
    if (a === undefined) continue
    const j = theirs.findIndex((b) => (exactOnly ? a.article === b.article : twin(a, b)))
    const b = theirs[j]
    if (b === undefined || j > i) continue
    const later = theirs.slice(j + 1)
    const kept = mine.slice(i + 1).every((m) => later.some((t) => twin(m, t)))
    if (kept) return { mine: a.at, theirs: b.at }
  }
  return null
}

/**
 * The overlap where an article does not show it — read worse in the next part: an article two digits
 * off, figures cut at its top edge, a name lost or split, a row cut through at the first part's foot
 * (review Р17, Р21, 9, Р23). The first part's last rows and the next part's first are aligned, row for
 * row in order (`sameRow`), the longest alignment that starts near the next part's top: the next part
 * goes on after the last row aligned, and the first part keeps its own reading of the overlap.
 */
function alignedSeam(
  rows: readonly TextRow[],
  next: readonly TextRow[],
  swaps: Readonly<Record<string, string>>,
): number | null {
  const tail = rows.flatMap((row, at) => (row.text.trim() === '' ? [] : [at])).slice(-OVERLAP_ROWS)
  const head = next
    .flatMap((row, at) => (row.text.trim() === '' ? [] : [at]))
    .slice(0, OVERLAP_ROWS)
  const mineText = (i: number) => rows[tail[i] ?? 0]?.text ?? ''
  const theirText = (j: number) => next[head[j] ?? 0]?.text ?? ''
  // rows read alike — an article misread only under one name, or, where one part lost the name, right
  // after the rows aligned above it (review Р21, Р26); and the figures right below an item's name read
  // alike in both are that item's, whatever article OCR made of them there
  const alike: boolean[][] = []
  const aligned = (i: number, j: number) => alike[i]?.[j] === true
  const placed = (i: number, j: number) => {
    const mineNamed = namesAbove(rows, tail[i] ?? 0).length > 0
    const theirNamed = namesAbove(next, head[j] ?? 0).length > 0
    if (mineNamed && !theirNamed) return aligned(i - 2, j - 1) || aligned(i - 3, j - 1)
    if (!mineNamed && theirNamed) return aligned(i - 1, j - 2) || aligned(i - 1, j - 3)
    return false
  }
  for (let i = 0; i < tail.length; i++) {
    const row: boolean[] = []
    alike.push(row)
    for (let j = 0; j < head.length; j++) {
      const [a, b] = [ARTICLE.exec(mineText(i))?.[1], ARTICLE.exec(theirText(j))?.[1]]
      const misreadOne =
        a !== undefined &&
        b !== undefined &&
        a !== b &&
        misread(a, b, swaps) &&
        (namesAgree(rows, tail[i] ?? 0, next, head[j] ?? 0) || placed(i, j))
      const unarticled = mineText(i).replace(ARTICLE, '')
      const theirs = theirText(j).replace(ARTICLE, '')
      const named =
        i > 0 && j > 0 && NUMBER.test(mineText(i - 1)) && sameRow(mineText(i - 1), theirText(j - 1))
      row.push(
        (misreadOne && likeness(unarticled, theirs) >= ALIKE) ||
          sameRow(mineText(i), theirText(j)) ||
          (named && a !== undefined && b !== undefined),
      )
    }
  }
  // the longest common run, in order, by rows read alike
  const best = Array.from({ length: tail.length + 1 }, () =>
    Array.from({ length: head.length + 1 }, () => 0),
  )
  for (let i = tail.length - 1; i >= 0; i--) {
    const row = best[i] ?? []
    for (let j = head.length - 1; j >= 0; j--) {
      row[j] = aligned(i, j)
        ? 1 + (best[i + 1]?.[j + 1] ?? 0)
        : Math.max(best[i + 1]?.[j] ?? 0, row[j + 1] ?? 0)
    }
  }
  // walk it, and keep where the next part's rows were last aligned
  let [i, j, first, last, mineLast] = [0, 0, -1, -1, -1]
  while (i < tail.length && j < head.length) {
    if (aligned(i, j)) {
      if (first < 0) first = j
      last = j
      mineLast = i
      i++
      j++
    } else if ((best[i + 1]?.[j] ?? 0) >= (best[i]?.[j + 1] ?? 0)) i++
    else j++
  }
  // the overlap is the first part's foot and the next part's top, or it is no overlap but rows alike
  // in the middle — a rule under the head and a rule above the total (review 11)
  if (last < 0 || first > OVERLAP_LEAD || tail.length - 1 - mineLast > OVERLAP_LEAD) return null
  return head[last] ?? null
}

export function mergeParts(
  reading: PhotoReading,
  parts: readonly (readonly TextRow[])[],
): TextRow[] {
  const { swaps } = reading
  let rows = [...(parts[0] ?? [])]
  for (const next of parts.slice(1)) {
    const seam = articleSeam(rows, next, true, swaps) ?? articleSeam(rows, next, false, swaps)
    if (seam !== null) {
      rows = [...rows.slice(0, seam.mine + 1), ...next.slice(seam.theirs + 1)]
      continue
    }
    const aligned = alignedSeam(rows, next, swaps)
    rows = [...rows, ...next.slice(aligned === null ? 0 : aligned + 1)]
  }
  return rows
}

/**
 * The receipt from its readings — Tesseract in two page modes, each with the parts merged: the
 * reading whose lines add up, else the one with more lines settled. The receipt's own arithmetic is
 * the judge; no truth is needed. Its total is one two sources vouch for (MOL-244, owner's В-1 «а»): two
 * places printed — the total and the payment (`votedTotal`) — else the lines that met it, one of them as
 * read, else none.
 * A total read in one place is a digit misread as often as not, the same in both readings (am-08, am-13,
 * am-36 on the bench), and a wrong sum on a trip is worse than none the person sees and types. What the
 * reading read stays `readTotalHundredths`, for «Прочитали не всё» alone.
 */
export function bestReading(
  reading: PhotoReading,
  readings: readonly (readonly TextRow[])[],
): ReceiptText {
  const lx = lexiconOf(reading)
  const laid = readings.map((rows) => readingOf(rows, lx))
  const parsed = laid.map((one) => one.read)
  const settledAt = (i: number): number => parsed[i]?.lines.filter((l) => l.settled).length ?? 0
  // a reading that met its total through a slice met its own choice (MOL-246): no balance to rank by
  // (review Д2)
  const metAt = (i: number): boolean => parsed[i]?.balanced === true && laid[i]?.sliced !== true
  const order = parsed
    .map((_, i) => i)
    .sort((a, b) => Number(metAt(b)) - Number(metAt(a)) || settledAt(b) - settledAt(a))
  const best = parsed[order[0] ?? 0]
  if (best === undefined) throw new RangeError('a receipt needs at least one reading')
  // no reading found a line: a section with no items is read whole, not one to shoot again (MOL-227)
  if (best.lines.length === 0) {
    const section = departmentReceipt(reading, readings)
    if (section !== null) return section
  }
  // Lines «meet» a total only as read: the total changes a line's digit to meet itself, or gives a line
  // with no reading its rest (adversarial А1). Every line as read — the lines vouch, unless two places print
  // another sum (Б1, Ц1); else two places printed (А1б); else the lines with one of them as read (owner).
  // a total met through a slice met its own choice (MOL-246, review Г1): only two places vouch for it
  const tookSlice = laid[parsed.indexOf(best)]?.sliced === true
  const asRead = (every: boolean): boolean =>
    best.balanced &&
    !tookSlice &&
    (every ? best.lines.every((l) => l.settled) : best.lines.some((l) => l.settled))
  const own = best.totalHundredths
  const voted = votedTotal(readings, false, lx)
  // the lines as read and two places naming two sums: either may be the font's slip, and nothing tells
  // which (Б1 against its mirror, round 3 Ц1) — so no total (owner, 09.10.2026)
  const total = asRead(true)
    ? voted === null || voted === own
      ? own
      : null
    : (voted ?? (asRead(false) ? own : null))
  if (total === own) return withTwins({ ...best, totalHundredths: total }, lx)
  // A total the reading did not judge its lines by leaves nothing in them, and judges none (review 4, 7):
  // the reading chosen is read again as printed. Two places shown are no judge either (В-2 «а»; round 5 Д1:
  // a vote misread alike in two places made a lost row up to meet itself, and the review's «≠» was gone) —
  // the lines are «balanced» only where, as printed, they add up to it; a row OCR lost stays empty (Г1).
  const reread = laid[parsed.indexOf(best)]?.asPrinted() ?? best
  const sums = reread.lines.map((line) => line.sumHundredths)
  const met =
    total !== null &&
    sums.every((sum) => sum !== null) &&
    sums.reduce((all, sum) => all + sum, 0) === total
  return withTwins(
    { ...reread, totalHundredths: total, readTotalHundredths: own, balanced: met },
    lx,
  )
}
